import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { initializeApp, type FirebaseApp } from 'firebase/app';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import {
  browserLocalPersistence,
  getAuth,
  getRedirectResult,
  GoogleAuthProvider,
  onAuthStateChanged,
  setPersistence,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  type User,
} from 'firebase/auth';
import './styles.css';
import './print-tools.css';
import {
  applyTheme,
  bootstrapTheme,
  readThemePreference,
  resolveTheme,
  saveThemePreference,
  type ResolvedTheme,
  type ThemePreference,
} from './theme';

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => undefined);
  });
}

type Step = 'home' | 'connect' | 'document' | 'review' | 'payment' | 'tracking' | 'ready' | 'collected';
type AppTab = 'home' | 'docs' | 'orders' | 'profile';
type ImageLayout = 'Full page' | 'Half page' | 'Quarter page' | 'Fill page' | 'Original size';
const imageLayoutOptions: ImageLayout[] = ['Full page', 'Half page', 'Quarter page', 'Fill page', 'Original size'];
type ImageRotation = 0 | 90 | 180 | 270;
const wordDocxContentType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
type IconName = 'arrow-left' | 'check' | 'chevron-right' | 'clock' | 'credit-card' | 'eye' | 'file' | 'folder' | 'home' | 'lock' | 'menu' | 'moon' | 'orders' | 'pencil' | 'pin' | 'printer' | 'qr' | 'refresh' | 'share' | 'shop' | 'sun' | 'trash' | 'upload' | 'user';

type DetectedBarcode = {
  rawValue: string;
};

type InstallPromptEvent = Event & {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

type InstallGuide = 'whatsapp' | 'install';

type BarcodeDetectorInstance = {
  detect(source: HTMLVideoElement): Promise<DetectedBarcode[]>;
};

type BarcodeDetectorConstructor = {
  new(options?: { formats?: string[] }): BarcodeDetectorInstance;
};

declare global {
  interface Window {
    BarcodeDetector?: BarcodeDetectorConstructor;
  }
}

interface SelectedDocument {
  file: File;
  url: string;
}

interface BatchDocumentItem {
  id: string;
  file: File;
  pages: number;
}

interface DocumentPreview {
  name: string;
  type: string;
  size: number;
  url: string;
  revokeOnClose: boolean;
  finalPageSetup?: boolean;
  managedDocument?: ManagedDocument;
}

interface CloudOrder {
  orderId: string;
  shopId: string;
  shopName?: string;
  fileName: string;
  checksum: string;
  shortChecksum: string;
  queueToken?: string;
  paymentReference: string;
  paymentMode?: string;
  pages: number;
  pageRange?: string;
  copies: number;
  paperSize: string;
  orientation: string;
  duplex?: boolean;
  imageLayout?: ImageLayout;
  imageRotation?: ImageRotation;
  accessToken?: string;
  mode: string;
  amount: number;
  status: string;
  createdAt?: string;
  updatedAt?: string;
  statusMessage?: string;
  cancelledBy?: string;
  cancelledAt?: string;
  convertedDocumentUrl?: string;
  convertedFromWord?: boolean;
  sourceFileName?: string;
  paymentSession?: PaymentSession;
}

interface PaymentSession {
  provider: string;
  providerOrderId: string;
  providerPaymentLinkId: string;
  checkoutUrl: string;
  qrPayload: string;
  expiresAt: string;
}

interface PublicShop {
  shopId: string;
  shopName: string;
  subscriptionStatus: string;
  printingPaused?: boolean;
  city?: string;
  address?: string;
  pinCode?: string;
  openingTime?: string;
  closingTime?: string;
  paperSizes?: string[];
  printModes?: string[];
  paymentMode?: 'manual' | 'gateway' | 'demo_pin' | 'counter_pin' | 'setup_required';
  checkoutMode?: 'manual' | 'razorpay' | 'demo_pin' | 'counter_pin' | 'setup_required';
  paymentAvailable?: boolean;
  agentAvailable?: boolean;
  pinSession?: { active: boolean; mode: 'demo_pin' | 'counter_pin' | ''; expiresAt: string };
  agentPaymentProvider?: 'manual' | 'razorpay' | 'cashfree' | 'paytm' | 'payu' | 'phonepe';
  manualPaymentEnabled?: boolean;
  wordConversionAvailable?: boolean;
  rates: {
    bw: number;
    color: number;
    byPaper?: Record<'A4' | 'Legal', { bw: number; color: number }>;
  };
}

interface CustomerProfile {
  uid: string;
  email: string;
  displayName: string;
  picture: string;
  createdAt: string;
  lastLoginAt: string;
  personas: { customer: boolean; shopkeeper: boolean; admin: boolean; tester: boolean };
}

interface ManagedDocument {
  id: string;
  name: string;
  type: string;
  size: number;
  folder: string;
  createdAt: number;
  blob: Blob;
}

const maxUploadBytes = 15 * 1024 * 1024;
const noActiveAgentMessage = 'This shop cannot accept print orders or payments because no print agent is active. Ask the shopkeeper to activate an Android or Windows agent.';
const imageOptimizationThresholdBytes = 2 * 1024 * 1024;
const imageOptimizationTargetBytes = 2 * 1024 * 1024;
const apiBase = (import.meta.env.VITE_CLOUD_API_URL || 'https://saralprint-cloud-api.azurewebsites.net/api').replace(/\/$/, '');
const recentOrderStorageKey = 'saralprint.currentOrder.v1';
const orderHistoryStorageKey = 'saralprint.orderHistory.v1';
const recentShopsStorageKey = 'saralprint.recentShops.v1';
const activeTabStorageKey = 'saralprint.activeTab.v1';
const customerPhoneStorageKey = 'saralprint.customerPhone.v1';
const startupSplashStorageKey = 'saralprint.startupSplashSeen.v1';
const documentFoldersStorageKey = 'saralprint.documentFolders.v1';
const browserBackStateKey = 'saralprintNavigation';
const browserExitConfirmationMs = 2_000;
const maxOrderHistory = 15;
const maxRecentShops = 5;
const defaultDocumentFolder = 'Personal';
const quickDocumentFolders = ['Personal', 'Work', 'School', 'Bills', 'Receipts', 'Other'];
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};
const hasFirebaseConfig = Object.values(firebaseConfig).every(Boolean);
let firebaseApp: FirebaseApp | null = null;
if (hasFirebaseConfig) firebaseApp = initializeApp(firebaseConfig);

function SplashScreen({ onComplete }: { onComplete: () => void }) {
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    const fallback = window.setTimeout(() => onCompleteRef.current(), 5_000);
    return () => window.clearTimeout(fallback);
  }, []);

  return (
    <div className="splash-screen" role="status" aria-label="Loading SaralPrint">
      <video
        className="splash-video"
        autoPlay
        muted
        playsInline
        preload="metadata"
        poster="/saral-print-icon.png"
        onLoadedMetadata={(event) => {
          event.currentTarget.defaultPlaybackRate = 2;
          event.currentTarget.playbackRate = 2;
        }}
        onEnded={onComplete}
        onError={onComplete}
      >
        <source src="/saral-print-splash.mp4" type="video/mp4" />
      </video>
      <div className="splash-brand" aria-hidden="true">
        <img src="/saral-print-icon.png" alt="" />
        <span>SARAL <b>PRINT</b></span>
      </div>
    </div>
  );
}

function App() {
  const [showSplash, setShowSplash] = useState(() => shouldShowStartupSplash());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const filePickerModeRef = useRef<'replace' | 'append'>('replace');
  const libraryFileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const scanLoopRef = useRef<number | null>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);
  const knownOrderStatusesRef = useRef(new Map<string, string>());
  const orderRefreshInProgressRef = useRef(false);
  const conversionDownloadInProgressRef = useRef(new Set<string>());
  const lastVisibilityRefreshAtRef = useRef(0);
  const gatewayHandoffRef = useRef(false);
  const gatewayPageHiddenRef = useRef(false);
  const appBackActionRef = useRef<() => boolean>(() => false);
  const lastRootBackAtRef = useRef(0);
  const [activeTab, setActiveTab] = useState<AppTab>(() => loadActiveTab());
  const [step, setStep] = useState<Step>('home');
  const [shopId, setShopId] = useState('');
  const [document, setDocument] = useState<SelectedDocument | null>(null);
  const [batchDocuments, setBatchDocuments] = useState<BatchDocumentItem[]>([]);
  const [batchBusy, setBatchBusy] = useState(false);
  const [startEachDocumentOnFront, setStartEachDocumentOnFront] = useState(false);
  const [choosingLibraryDocuments, setChoosingLibraryDocuments] = useState(false);
  const [managedDocuments, setManagedDocuments] = useState<ManagedDocument[]>([]);
  const [customFolders, setCustomFolders] = useState<string[]>(() => loadDocumentFolders());
  const [folderFilter, setFolderFilter] = useState('All');
  const [newFolderName, setNewFolderName] = useState(defaultDocumentFolder);
  const [folderNameDraft, setFolderNameDraft] = useState('');
  const [editingDocument, setEditingDocument] = useState<ManagedDocument | null>(null);
  const [documentNameDraft, setDocumentNameDraft] = useState('');
  const [documentFolderDraft, setDocumentFolderDraft] = useState(defaultDocumentFolder);
  const [pages, setPages] = useState(1);
  const [documentPageCount, setDocumentPageCount] = useState(1);
  const [pageRange, setPageRange] = useState('');
  const [copies, setCopies] = useState(1);
  const [customerPhone, setCustomerPhone] = useState(() => loadCustomerPhone());
  const [paymentPin, setPaymentPin] = useState('');
  const [paymentPreference, setPaymentPreference] = useState<'online' | 'manual'>('online');
  const [pinBusy, setPinBusy] = useState(false);
  const [mode, setMode] = useState<'bw' | 'color'>('bw');
  const [paperSize, setPaperSize] = useState<'A4' | 'Legal'>('A4');
  const [orientation, setOrientation] = useState<'Auto' | 'Portrait' | 'Landscape'>('Auto');
  const [duplex, setDuplex] = useState(false);
  const [imageLayout, setImageLayout] = useState<ImageLayout>('Full page');
  const [imageRotation, setImageRotation] = useState<ImageRotation>(0);
  const [order, setOrder] = useState<CloudOrder | null>(null);
  const [recentOrder, setRecentOrder] = useState<CloudOrder | null>(null);
  const [orderHistory, setOrderHistory] = useState<CloudOrder[]>([]);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [wordConversionElapsedSeconds, setWordConversionElapsedSeconds] = useState(0);
  const [paymentPreparationSeconds, setPaymentPreparationSeconds] = useState(0);
  const [paymentConfirmationSeconds, setPaymentConfirmationSeconds] = useState(0);
  const [gatewayHandoffInProgress, setGatewayHandoffInProgress] = useState(false);
  const [currentTime, setCurrentTime] = useState(() => Date.now());
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<DocumentPreview | null>(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanMessage, setScanMessage] = useState('');
  const [selectedShop, setSelectedShop] = useState<PublicShop | null>(null);
  const [shopChecking, setShopChecking] = useState(false);
  const [themePreference, setThemePreference] = useState<ThemePreference>(() => readThemePreference());
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolveTheme(readThemePreference()));
  const [customerUser, setCustomerUser] = useState<User | null>(null);
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState('');
  const [customerProfile, setCustomerProfile] = useState<CustomerProfile | null>(null);
  const [recentShops, setRecentShops] = useState<PublicShop[]>(() => loadRecentShops());
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(null);
  const [appInstalled, setAppInstalled] = useState(() => isStandaloneApp());
  const [installGuide, setInstallGuide] = useState<InstallGuide | null>(null);
  const [installOfferDismissed, setInstallOfferDismissed] = useState(false);

  const selectedShopId = cleanShop(shopId);
  const hasVerifiedShop = Boolean(selectedShop && cleanShop(selectedShop.shopId) === selectedShopId);
  const shopOpenNow = isShopOpenNow(selectedShop, currentTime);
  const legalPaperEnabled = selectedShop?.paperSizes?.includes('Legal') === true;
  const colorPrintingEnabled = selectedShop?.printModes?.includes('Color') === true;
  const paperSizeOptions = useMemo<('A4' | 'Legal')[]>(() => legalPaperEnabled ? ['A4', 'Legal'] : ['A4'], [legalPaperEnabled]);
  const printModeOptions = ['B/W', 'Colour'];
  const rate = shopPageRate(selectedShop, paperSize, mode);
  const amount = rate * pages * copies;
  const pageRangeSelection = useMemo(
    () => parsePageRange(pageRange, documentPageCount),
    [pageRange, documentPageCount],
  );
  const folderNames = useMemo(() => {
    const names = Array.from(new Set([
      defaultDocumentFolder,
      ...customFolders,
      ...managedDocuments.map((item) => item.folder || defaultDocumentFolder),
    ]));
    return ['All', ...names.sort((a, b) => a.localeCompare(b))];
  }, [customFolders, managedDocuments]);
  const uploadFolderNames = useMemo(() => Array.from(new Set([...quickDocumentFolders, ...folderNames.filter((folder) => folder !== 'All')])).sort((left, right) => left.localeCompare(right)), [folderNames]);
  const visibleManagedDocuments = useMemo(() => {
    if (folderFilter === 'All') return managedDocuments;
    return managedDocuments.filter((item) => item.folder === folderFilter);
  }, [folderFilter, managedDocuments]);
  const recentDocuments = managedDocuments.slice(0, 3);
  const activeOrders = orderHistory.filter((item) => !['PRINTED', 'COLLECTED', 'FAILED'].includes(item.status));
  const readyOrders = orderHistory.filter((item) => item.status === 'PRINTED');
  const failedOrders = orderHistory.filter((item) => item.status === 'FAILED');
  const completedOrders = orderHistory.filter((item) => item.status === 'COLLECTED');
  const pendingPaymentCount = orderHistory.filter((item) => ['AWAITING_PAYMENT', 'PAYMENT_SESSION_CREATED', 'PAYMENT_PENDING', 'PAYMENT_EXPIRED'].includes(item.status)).length;
  // The dashboard is for orders that still need customer attention. Printed
  // and completed orders remain available in My Orders instead.
  const hideBottomNavigation = activeTab === 'home' && ['connect', 'document', 'review', 'payment'].includes(step);

  const checkoutUrl = safeCheckoutUrl(order?.paymentSession?.checkoutUrl);
  const shopCheckoutMode = selectedShop?.checkoutMode || (selectedShop?.paymentMode === 'gateway' ? 'razorpay' : selectedShop?.paymentMode);
  const manualPaymentShop = shopCheckoutMode === 'manual' || shopCheckoutMode === 'demo_pin' || shopCheckoutMode === 'counter_pin' || selectedShop?.paymentMode === 'manual';
  const manualPaymentChoice = selectedShop?.manualPaymentEnabled === true && (shopCheckoutMode === 'razorpay' || shopCheckoutMode === 'manual');
  const selectedPaymentPreference = shopCheckoutMode === 'manual' || (manualPaymentChoice && paymentPreference === 'manual') ? 'manual' : 'online';
  const paymentAvailable = Boolean(selectedShop?.paymentAvailable ?? (shopCheckoutMode && shopCheckoutMode !== 'setup_required'));
  const agentAvailable = selectedShop?.agentAvailable === true;
  const customerPhoneRequired = false;
  const customerPhoneGatewayName = selectedShop?.agentPaymentProvider === 'cashfree'
    ? 'Cashfree'
    : selectedShop?.agentPaymentProvider === 'paytm'
      ? 'Paytm'
      : selectedShop?.agentPaymentProvider === 'payu'
        ? 'PayU'
        : 'this payment method';
  const pinPaymentOrder = order?.paymentMode === 'DEMO_PIN' || order?.paymentMode === 'COUNTER_PIN';
  const demoPinOrder = order?.paymentMode === 'DEMO_PIN';
  const manualPaymentOrder = pinPaymentOrder || order?.paymentMode === 'MANUAL' || order?.paymentMode === 'MANUAL_CASHIER';
  const paymentConfirmationPending = order?.status === 'PAYMENT_PENDING';
  const paymentLinkReady = Boolean(checkoutUrl) && ['PAYMENT_SESSION_CREATED', 'AWAITING_PAYMENT'].includes(order?.status || '');
  const paymentNeedsAction = ['AWAITING_PAYMENT', 'PAYMENT_SESSION_CREATED', 'PAYMENT_EXPIRED'].includes(order?.status || '');
  const paymentPhoneValid = /^[6-9]\d{9}$/.test(customerPhone);
  const selectedPageLabel = documentPageCount === 1 ? 'All pages' : `All pages (${documentPageCount})`;
  const preparingPaymentLink = Boolean(order && step === 'payment' && agentAvailable && !manualPaymentOrder && paymentNeedsAction && order.status !== 'PAYMENT_EXPIRED' && !checkoutUrl);
  const wordConversionInProgress = Boolean(order && ['CONVERSION_PENDING', 'CONVERTING'].includes(order.status));
  const showWordConversionProgress = wordConversionInProgress && activeTab === 'home' && ['review', 'tracking'].includes(step);

  useEffect(() => {
    if (paperSize === 'Legal' && !legalPaperEnabled) setPaperSize('A4');
  }, [legalPaperEnabled, paperSize]);

  useEffect(() => {
    const timer = window.setInterval(() => setCurrentTime(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const displayMode = window.matchMedia('(display-mode: standalone)');
    const captureInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as InstallPromptEvent);
    };
    const markInstalled = () => {
      setAppInstalled(true);
      setInstallPrompt(null);
      setInstallOfferDismissed(false);
    };
    const updateDisplayMode = () => setAppInstalled(isStandaloneApp());

    window.addEventListener('beforeinstallprompt', captureInstallPrompt);
    window.addEventListener('appinstalled', markInstalled);
    displayMode.addEventListener?.('change', updateDisplayMode);
    return () => {
      window.removeEventListener('beforeinstallprompt', captureInstallPrompt);
      window.removeEventListener('appinstalled', markInstalled);
      displayMode.removeEventListener?.('change', updateDisplayMode);
    };
  }, []);

  useEffect(() => {
    if (mode === 'color' && !colorPrintingEnabled) setMode('bw');
  }, [colorPrintingEnabled, mode]);

  useEffect(() => {
    if (/^[6-9]\d{9}$/.test(customerPhone)) saveCustomerPhone(customerPhone);
  }, [customerPhone]);

  useEffect(() => {
    const markGatewayPageHidden = () => {
      gatewayPageHiddenRef.current = true;
    };
    const restoreAfterGateway = (event: PageTransitionEvent) => {
      if (!gatewayPageHiddenRef.current && !event.persisted) return;
      gatewayPageHiddenRef.current = false;
      clearGatewayHandoff();
    };
    window.addEventListener('pagehide', markGatewayPageHidden);
    window.addEventListener('pageshow', restoreAfterGateway);
    return () => {
      window.removeEventListener('pagehide', markGatewayPageHidden);
      window.removeEventListener('pageshow', restoreAfterGateway);
    };
  }, []);

  useEffect(() => {
    if (!message) return;
    const timer = window.setTimeout(() => setMessage(''), 5_000);
    return () => window.clearTimeout(timer);
  }, [message]);

  useEffect(() => {
    bootstrapTheme();
    setResolvedTheme(resolveTheme(readThemePreference()));

    const params = new URLSearchParams(window.location.search);
    const shop = params.get('shop') || params.get('code');
    const returnedOrderId = params.get('order') || params.get('orderId');
    const orderId = returnedOrderId && !['null', 'undefined'].includes(returnedOrderId.toLowerCase())
      ? returnedOrderId
      : '';
    const shared = params.get('shared');
    const shareError = params.get('shareError');
    const restoredShops = loadRecentShops();
    const restoredHistory = loadOrderHistory();
    const restoredOrder = loadRecentOrder() || restoredHistory[0] || null;
    restoredHistory.forEach((item) => knownOrderStatusesRef.current.set(`${item.shopId}:${item.orderId}`, item.status));
    if (restoredOrder) knownOrderStatusesRef.current.set(`${restoredOrder.shopId}:${restoredOrder.orderId}`, restoredOrder.status);
    setOrderHistory(restoredHistory);
    if (restoredOrder) setRecentOrder(restoredOrder);
    void reloadManagedDocuments();

    if (Number(shared) > 0) {
      setActiveTab('docs');
      setFolderFilter('Shared');
      setMessage(`${shared} shared document${shared === '1' ? '' : 's'} added to the Shared folder.`);
    } else if (shareError) {
      setActiveTab('docs');
      setFolderFilter('Shared');
      setMessage(shareError === 'unsupported'
        ? 'The shared item was not a supported PDF, DOCX or image.'
        : 'Google shared a link instead of the image. Open the full image and share it again, or download it first.');
    }

    if (shop) {
      setActiveTab('home');
      const nextShopId = cleanShop(shop);
      setShopId(nextShopId);
      if (orderId) {
        // A payment-provider return contains the order and shop ids, but does
        // not otherwise hydrate the selected-shop view. Restore it separately
        // so completing an order returns the customer to that same shop.
        void restoreSelectedShop(nextShopId);
        const storedOrder = [restoredOrder, ...restoredHistory].find((item): item is CloudOrder => Boolean(
          item && item.orderId === orderId && cleanShop(item.shopId) === nextShopId,
        ));
        setMessage('Returned from payment. Checking status...');
        setStep('tracking');
        void loadOrderFromUrl(orderId, nextShopId, storedOrder);
      } else {
        setMessage('Verifying shop...');
        void connectShop(nextShopId, setMessage).then((connected) => {
          if (connected) setStep('home');
        });
      }
    } else if (restoredShops[0]) {
      // Keep the most recently used shop connected across ordinary app reloads.
      void restoreSelectedShop(restoredShops[0].shopId);
    }

    if (window.location.search) {
      window.history.replaceState({}, '', '/');
    }

    return () => stopQrScanner();
  }, []);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(activeTabStorageKey, activeTab);
    } catch {
      // Navigation persistence is a convenience; private browsing may block it.
    }
  }, [activeTab]);

  useEffect(() => {
    const currentState = window.history.state && typeof window.history.state === 'object'
      ? window.history.state
      : {};
    const rootState = { ...currentState, [browserBackStateKey]: 'root' };
    const guardState = { ...currentState, [browserBackStateKey]: 'guard' };
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    // Keep one same-page history entry in front of the app. Android/browser
    // Back first returns to the root marker, where the current in-app screen
    // can be closed safely instead of unexpectedly leaving Saral Print.
    window.history.replaceState(rootState, '', currentUrl);
    window.history.pushState(guardState, '', currentUrl);

    const rearmBackGuard = () => window.history.pushState(guardState, '', currentUrl);
    const handleBrowserBack = () => {
      if (appBackActionRef.current()) {
        lastRootBackAtRef.current = 0;
        rearmBackGuard();
        return;
      }

      const now = Date.now();
      if (now - lastRootBackAtRef.current <= browserExitConfirmationMs) {
        lastRootBackAtRef.current = 0;
        window.history.back();
        return;
      }

      lastRootBackAtRef.current = now;
      setMessage('Press back again to leave Saral Print.');
      rearmBackGuard();
    };

    window.addEventListener('popstate', handleBrowserBack);
    return () => window.removeEventListener('popstate', handleBrowserBack);
  }, []);

  useEffect(() => {
    if (activeTab !== 'docs' && !previewDocument) return;
    const warnBeforeRefresh = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = true;
    };
    window.addEventListener('beforeunload', warnBeforeRefresh);
    return () => window.removeEventListener('beforeunload', warnBeforeRefresh);
  }, [activeTab, previewDocument]);

  useEffect(() => {
    if (!firebaseApp) return;
    const auth = getAuth(firebaseApp);
    void setPersistence(auth, browserLocalPersistence).catch(() => undefined);
    void getRedirectResult(auth).catch((error: unknown) => {
      setAuthError(customerAuthError(error));
    });
    return onAuthStateChanged(auth, (nextUser) => {
      setCustomerUser(nextUser);
      setAuthBusy(false);
      if (nextUser) {
        setAuthError('');
        void syncCustomerAccount(nextUser);
      } else {
        setCustomerProfile(null);
      }
    });
  }, []);

  async function syncCustomerAccount(activeUser: User) {
    try {
      const idToken = await activeUser.getIdToken();
      const headers = { authorization: `Bearer ${idToken}` };
      const [profileResponse, ordersResponse] = await Promise.all([
        fetch(`${apiBase}/customers/me`, { headers }),
        fetch(`${apiBase}/customers/me/orders`, { headers }),
      ]);
      const profile = await profileResponse.json() as CustomerProfile & { error?: string };
      const ordersBody = await ordersResponse.json() as { orders?: CloudOrder[]; error?: string };
      if (!profileResponse.ok) throw new Error(profile.error || 'Customer profile could not be loaded.');
      if (!ordersResponse.ok) throw new Error(ordersBody.error || 'Customer order history could not be loaded.');
      setCustomerProfile(profile);
      setOrderHistory((current) => {
        const localTokens = new Map(current.map((item) => [`${item.shopId}:${item.orderId}`, item.accessToken]));
        const merged = [...(ordersBody.orders || []).map((item) => ({
          ...item,
          accessToken: localTokens.get(`${item.shopId}:${item.orderId}`),
        })), ...current];
        const unique = merged.filter((item, index) => merged.findIndex((candidate) => candidate.shopId === item.shopId && candidate.orderId === item.orderId) === index).slice(0, maxOrderHistory);
        saveOrderHistory(unique);
        return unique;
      });
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : 'Customer profile could not be loaded.');
    }
  }

  async function loginCustomer() {
    if (!firebaseApp) {
      setAuthError('Customer sign-in is not configured for this build.');
      return;
    }

    setAuthBusy(true);
    setAuthError('');
    const auth = getAuth(firebaseApp);
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    try {
      await setPersistence(auth, browserLocalPersistence);
      await signInWithPopup(auth, provider);
    } catch (error: unknown) {
      const code = typeof error === 'object' && error && 'code' in error ? String(error.code) : '';
      if (code === 'auth/popup-blocked' || code === 'auth/cancelled-popup-request') {
        try {
          await signInWithRedirect(auth, provider);
          return;
        } catch (redirectError) {
          setAuthError(customerAuthError(redirectError));
        }
      } else {
        setAuthError(customerAuthError(error));
      }
      setAuthBusy(false);
    }
  }

  async function logoutCustomer() {
    if (!firebaseApp) return;
    await signOut(getAuth(firebaseApp));
    setMessage('Signed out. You can continue printing as a guest.');
  }

  useEffect(() => {
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!media) return;

    const syncSystemTheme = () => {
      if (themePreference !== 'system') return;
      applyTheme('system');
      setResolvedTheme(resolveTheme('system'));
    };

    syncSystemTheme();
    media.addEventListener('change', syncSystemTheme);
    return () => media.removeEventListener('change', syncSystemTheme);
  }, [themePreference]);

  function setTheme(nextTheme: ThemePreference) {
    setThemePreference(nextTheme);
    saveThemePreference(nextTheme);
    setResolvedTheme(resolveTheme(nextTheme));
  }

  function toggleTheme() {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
  }

  useEffect(() => {
    if (!order || ['COLLECTED', 'FAILED'].includes(order.status)) return;
    const convertingDocument = ['CONVERSION_PENDING', 'CONVERTING'].includes(order.status);
    const awaitingPayment = ['AWAITING_PAYMENT', 'PAYMENT_SESSION_CREATED', 'PAYMENT_PENDING', 'PAYMENT_EXPIRED'].includes(order.status);
    const waitingForPaymentLink = awaitingPayment && !safeCheckoutUrl(order.paymentSession?.checkoutUrl);
    const activelyPrinting = ['PAID', 'AGENT_DOWNLOADING', 'READY_TO_PRINT', 'PRINTING'].includes(order.status);
    const readyForCollection = order.status === 'PRINTED';
    const refreshInterval = convertingDocument || waitingForPaymentLink ? 1000 : awaitingPayment ? 3000 : activelyPrinting ? 1500 : readyForCollection ? 5000 : 10000;
    const timer = window.setInterval(() => {
      if (window.document.visibilityState === 'visible') void refreshOrder(false);
    }, refreshInterval);
    return () => window.clearInterval(timer);
  }, [order?.orderId, order?.shopId, order?.status, order?.paymentSession?.checkoutUrl]);

  useEffect(() => {
    const activeShopId = selectedShop?.shopId;
    if (!activeShopId || !['review', 'payment', 'tracking'].includes(step)) return;

    const refreshAgentAvailability = async () => {
      if (window.document.visibilityState !== 'visible') return;
      try {
        const shop = await loadShop(activeShopId);
        setSelectedShop((current) => current?.shopId === activeShopId ? shop : current);
      } catch {
        // The cloud remains authoritative when the customer takes an action.
      }
    };

    void refreshAgentAvailability();
    const timer = window.setInterval(() => void refreshAgentAvailability(), 5000);
    return () => window.clearInterval(timer);
  }, [selectedShop?.shopId, step]);

  useEffect(() => {
    if (!order?.convertedDocumentUrl || order.status !== 'CONVERSION_READY' || !['review', 'payment', 'tracking'].includes(step)) return;
    const conversionKey = `${order.shopId}:${order.orderId}`;
    if (conversionDownloadInProgressRef.current.has(conversionKey)) return;
    conversionDownloadInProgressRef.current.add(conversionKey);

    void (async () => {
      try {
        setMessage('PDF preview is ready. Downloading it securely...');
        const response = await fetch(order.convertedDocumentUrl!, { cache: 'no-store' });
        if (!response.ok) throw new Error('Converted PDF download failed. Refresh and try again.');
        const blob = await response.blob();
        const file = new File([blob], order.fileName || 'converted-document.pdf', { type: 'application/pdf' });
        const detectedPages = await detectDocumentPages(file);
        const url = URL.createObjectURL(file);
        setDocument((current) => {
          if (current) URL.revokeObjectURL(current.url);
          return { file, url };
        });
        setBatchDocuments([]);
        setStartEachDocumentOnFront(false);
        setPages(detectedPages);
        setDocumentPageCount(detectedPages);
        setPageRange('');
        setStep('review');
        await saveManagedDocument(file, 'Converted PDFs');
        await reloadManagedDocuments();
        setMessage(`PDF preview ready: ${detectedPages} page${detectedPages === 1 ? '' : 's'}. It is saved in Docs for next time.`);
      } catch (error) {
        conversionDownloadInProgressRef.current.delete(conversionKey);
        setMessage(error instanceof Error ? error.message : 'Could not open the converted PDF.');
      }
    })();
  }, [order?.orderId, order?.shopId, order?.status, order?.convertedDocumentUrl, step]);

  useEffect(() => {
    const awaitingLink = order &&
      !manualPaymentOrder &&
      order.status !== 'PAYMENT_EXPIRED' &&
      ['AWAITING_PAYMENT', 'PAYMENT_SESSION_CREATED', 'PAYMENT_PENDING'].includes(order.status) &&
      !safeCheckoutUrl(order.paymentSession?.checkoutUrl);
    if (!awaitingLink || !order.accessToken || !agentAvailable) return;

    // MQTT normally wakes the agent immediately. Re-publish a single wake-up
    // after five seconds if the broker delivery or an in-flight agent refresh
    // was delayed, without increasing the normal polling frequency.
    const timer = window.setTimeout(() => {
      if (window.document.visibilityState === 'visible') void requestPaymentLink(order);
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [order?.orderId, order?.status, order?.paymentSession?.checkoutUrl, order?.accessToken, manualPaymentOrder, agentAvailable]);

  useEffect(() => {
    if (!order || ['COLLECTED', 'FAILED'].includes(order.status)) return;
    const refreshWhenVisible = () => {
      const now = Date.now();
      if (window.document.visibilityState !== 'visible' || now - lastVisibilityRefreshAtRef.current < 500) return;
      lastVisibilityRefreshAtRef.current = now;
      void refreshOrder(false);
    };
    window.addEventListener('focus', refreshWhenVisible);
    window.document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      window.removeEventListener('focus', refreshWhenVisible);
      window.document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [order?.orderId, order?.shopId, order?.status]);

  useEffect(() => {
    if (order?.status === 'PRINTED' && step === 'tracking') {
      setStep('ready');
    }
    if (order?.status === 'COLLECTED' && ['tracking', 'ready'].includes(step)) {
      setStep('collected');
    }
  }, [order?.status, step]);

  useEffect(() => {
    if (!wordConversionInProgress) {
      setWordConversionElapsedSeconds(0);
      return;
    }

    const startedAt = Date.now();
    setWordConversionElapsedSeconds(0);
    const timer = window.setInterval(() => {
      setWordConversionElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 250);
    return () => window.clearInterval(timer);
    // Pending -> converting must remain one continuous progress journey.
  }, [order?.orderId, wordConversionInProgress]);

  useEffect(() => {
    if (!preparingPaymentLink) {
      setPaymentPreparationSeconds(0);
      return;
    }

    const startedAt = Date.now();
    setPaymentPreparationSeconds(20);
    const timer = window.setInterval(() => {
      const remaining = Math.max(0, 20 - Math.floor((Date.now() - startedAt) / 1000));
      setPaymentPreparationSeconds(remaining);
      if (remaining === 0) window.clearInterval(timer);
    }, 250);
    return () => window.clearInterval(timer);
  // Do not restart the visible progress every time the background retry runs.
  // The customer should see one continuous preparation journey per order.
  }, [order?.orderId, preparingPaymentLink]);

  useEffect(() => {
    if (!paymentConfirmationPending) {
      setPaymentConfirmationSeconds(0);
      return;
    }

    const startedAt = Date.now();
    const refreshEverySeconds = 3;
    setPaymentConfirmationSeconds(refreshEverySeconds);
    const timer = window.setInterval(() => {
      const elapsedSeconds = Math.floor((Date.now() - startedAt) / 1000);
      setPaymentConfirmationSeconds(refreshEverySeconds - (elapsedSeconds % refreshEverySeconds));
    }, 250);
    return () => window.clearInterval(timer);
  }, [order?.orderId, paymentConfirmationPending]);

  useEffect(() => {
    if (!order) return;
    if (['PAID', 'AGENT_DOWNLOADING', 'READY_TO_PRINT', 'PRINTING'].includes(order.status) && (step === 'payment' || step === 'review')) {
      setStep('tracking');
      setMessage(order.paymentMode === 'DEMO_PIN'
        ? 'Demo approved. No payment was collected; your order is being prepared.'
        : order.paymentMode === 'COUNTER_PIN'
          ? 'Counter payment approved. Your order is being prepared.'
          : order.paymentMode === 'MANUAL_CASHIER' ? 'Manual payment confirmed. Your order is being prepared.' : 'Payment received. Your order is being prepared.');
    }
  }, [order?.status, order?.orderId, step]);

  async function confirmShop() {
    const nextShopId = cleanShop(shopId);
    if (!nextShopId) {
      setMessage('Enter the shop code shown by the print shop.');
      return;
    }

    if (await connectShop(nextShopId, setMessage)) continueAfterShopConnection();
  }

  function continueAfterShopConnection() {
    stopQrScanner();
    setActiveTab('home');
    setStep(document ? 'review' : 'document');
  }

  async function applyShopQr(rawValue: string) {
    const code = readShopCode(rawValue);
    if (!code) {
      setScanMessage('QR did not contain a shop code.');
      return;
    }

    const nextShopId = cleanShop(code);
    const connected = await connectShop(nextShopId, setScanMessage);
    if (connected) continueAfterShopConnection();
  }

  async function connectShop(nextShopId: string, report: (value: string) => void) {
    if (nextShopId === 'SP-DEMO-001') {
      report('SP-DEMO-001 is only a demo code. Ask the shopkeeper to sign in and share the cloud shop id.');
      setSelectedShop(null);
      return false;
    }

    setShopChecking(true);
    report('Checking shop id...');
    try {
      const shop = await loadShop(nextShopId);
      if (shop.subscriptionStatus && shop.subscriptionStatus !== 'Active') {
        report('This shop is not active right now.');
        setSelectedShop(null);
        return false;
      }

      setShopId(shop.shopId);
      setSelectedShop(shop);
      setRecentShops((current) => {
        const next = [shop, ...current.filter((item) => item.shopId !== shop.shopId)].slice(0, maxRecentShops);
        saveRecentShops(next);
        return next;
      });
      setMessage(`Connected to ${shop.shopName || shop.shopId}.`);
      setScanMessage('');
      return true;
    } catch (error) {
      report(error instanceof Error ? error.message : 'Shop id could not be verified.');
      setSelectedShop(null);
      return false;
    } finally {
      setShopChecking(false);
    }
  }

  async function restoreSelectedShop(nextShopId: string) {
    try {
      const shop = await loadShop(nextShopId);
      if (shop.subscriptionStatus && shop.subscriptionStatus !== 'Active') return;
      setShopId(shop.shopId);
      setSelectedShop(shop);
      setRecentShops((current) => {
        const next = [shop, ...current.filter((item) => item.shopId !== shop.shopId)].slice(0, maxRecentShops);
        saveRecentShops(next);
        return next;
      });
    } catch {
      // The customer can reconnect manually if the shop is no longer available.
    }
  }

  async function selectRecentShop(shop: PublicShop) {
    const continuePrinting = step === 'connect' || Boolean(document) || !selectedShop;
    setShopId(shop.shopId);
    const connected = await connectShop(shop.shopId, setMessage);
    if (!connected) return;
    if (continuePrinting) continueAfterShopConnection();
    else {
      setActiveTab('home');
      setStep('home');
    }
  }

  function renderRecentShops(label = 'Recent shops', excludeSelected = false) {
    const shops = excludeSelected
      ? recentShops.filter((shop) => shop.shopId !== selectedShop?.shopId)
      : recentShops;
    if (!shops.length) return null;
    return (
      <section className="recent-shop-strip" aria-label="Recently selected shops">
        <div className="recent-shop-heading">
          <span>{label}</span>
          <small>Last used first</small>
        </div>
        <div className="recent-shop-list">{shops.map((shop, index) => (
          <button
            type="button"
            key={shop.shopId}
            disabled={shopChecking}
            onClick={() => selectRecentShop(shop)}
            title={shop.shopId}
            aria-label={`${index === 0 ? 'Last used shop' : `Recent shop ${index + 1}`}: ${shop.shopName || shop.shopId}`}
          >
            <span className="recent-shop-position" aria-hidden="true">{index + 1}</span>
            <Icon name="shop" /><span>{shop.shopName || shop.shopId}</span>
            {index === 0 && <small>Last used</small>}
          </button>
        ))}</div>
      </section>
    );
  }

  async function startQrScanner() {
    stopQrScanner();
    setScannerOpen(true);
    setScanMessage('Requesting camera permission...');

    if (!window.isSecureContext) {
      setScanMessage('Camera needs HTTPS. Use phone camera or enter the shop code.');
      setScannerOpen(false);
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setScanMessage('Camera is not available in this browser. Enter the shop code manually.');
      setScannerOpen(false);
      return;
    }

    try {
      const video = await waitForVideo(videoRef);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      cameraStreamRef.current = stream;
      video.srcObject = stream;
      video.setAttribute('playsinline', 'true');
      video.muted = true;
      await video.play();

      if (!window.BarcodeDetector) {
        setScanMessage('Camera opened, but this browser cannot scan QR inside the app. Use phone camera or enter the shop code.');
        return;
      }

      setScanMessage('Point camera at the shop QR.');
      const detector = new window.BarcodeDetector({ formats: ['qr_code'] });

      const scan = async () => {
        if (!videoRef.current) return;
        try {
          const codes = await detector.detect(videoRef.current);
          if (codes[0]?.rawValue) {
            applyShopQr(codes[0].rawValue);
            return;
          }
        } catch {
          setScanMessage('Could not read QR yet. Hold steady.');
        }
        scanLoopRef.current = window.setTimeout(scan, 500);
      };

      void scan();
    } catch (error) {
      stopQrScanner();
      setScanMessage(cameraErrorMessage(error));
    }
  }

  function stopQrScanner() {
    if (scanLoopRef.current) {
      window.clearTimeout(scanLoopRef.current);
      scanLoopRef.current = null;
    }

    cameraStreamRef.current?.getTracks().forEach((track) => track.stop());
    cameraStreamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setScannerOpen(false);
  }

  async function selectDocument(files: FileList | File[] | null, pickerMode = filePickerModeRef.current) {
    const selectedFiles = Array.from(files ?? []).map(normalizeDocumentFile);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!selectedFiles.length) return;

    const appending = pickerMode === 'append';
    const existingItems = appending ? batchDocuments : [];
    if (selectedFiles.some((file) => !isSupportedDocument(file))) {
      setMessage('Only PDF, DOCX, JPG, PNG and WEBP are allowed.');
      return;
    }
    if (selectedFiles.some((file) => file.size > maxUploadBytes)) {
      setMessage('Each selected document must be 15 MB or less.');
      return;
    }
    if ((existingItems.length + selectedFiles.length) > 10) {
      setMessage('You can combine up to 10 documents in one print job.');
      return;
    }
    if ((existingItems.length > 0 || selectedFiles.length > 1) && selectedFiles.some(isWordDocument)) {
      setMessage('DOCX files currently need to be converted and printed one at a time. Select PDFs or images for a combined job.');
      return;
    }

    setBatchBusy(true);
    setMessage(selectedFiles.length > 1 || appending ? 'Preparing combined PDF...' : 'Reading document...');
    try {
      const newItems: BatchDocumentItem[] = [];
      for (const file of selectedFiles) {
        newItems.push({ id: createBatchDocumentId(), file, pages: await detectDocumentPages(file) });
      }
      const nextItems = [...existingItems, ...newItems];
      if (nextItems.reduce((total, item) => total + item.pages, 0) > 200) {
        throw new Error('A combined print job can contain up to 200 source pages.');
      }

      if (nextItems.length === 1 && isWordDocument(nextItems[0].file)) {
        setSelectedDocument(nextItems[0].file, nextItems[0].pages);
        setBatchDocuments([]);
      } else {
        await applyBatchDocuments(nextItems, duplex && startEachDocumentOnFront);
      }
      setCopies(1);
      setOrder(null);
      setMessage(nextItems.length > 1
        ? `${nextItems.length} documents combined. Arrange them or preview the final PDF.`
        : 'Document ready. Check pages, print type and price.');
      setStep(hasVerifiedShop ? 'review' : 'connect');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Could not prepare the selected documents.');
    } finally {
      setBatchBusy(false);
    }
  }

  function setSelectedDocument(file: File, detectedPages: number) {
    if (document) URL.revokeObjectURL(document.url);
    setDocument({ file, url: URL.createObjectURL(file) });
    setPages(detectedPages);
    setDocumentPageCount(detectedPages);
    setPageRange('');
    setImageLayout('Full page');
    setImageRotation(0);
  }

  async function applyBatchDocuments(items: BatchDocumentItem[], insertFrontSideBlanks: boolean) {
    if (!items.length) {
      if (document) URL.revokeObjectURL(document.url);
      setDocument(null);
      setBatchDocuments([]);
      setPages(1);
      setDocumentPageCount(1);
      setPageRange('');
      setStep('document');
      return;
    }

    let file = items[0].file;
    let pageCount = items[0].pages;
    if (items.length > 1) {
      const merged = await mergeDocumentsAsPdf(items, insertFrontSideBlanks);
      if (merged.file.size > maxUploadBytes) {
        throw new Error('The combined PDF is larger than 15 MB. Remove one or more documents and try again.');
      }
      file = merged.file;
      pageCount = merged.pages;
    }
    setSelectedDocument(file, pageCount);
    setBatchDocuments(items);
    setOrder(null);
    setPaymentPin('');
  }

  function openDocumentPicker() {
    filePickerModeRef.current = 'replace';
    fileInputRef.current?.click();
  }

  function openAdditionalDocumentPicker() {
    filePickerModeRef.current = 'append';
    fileInputRef.current?.click();
  }

  function openSavedDocumentPicker() {
    setChoosingLibraryDocuments(true);
    setActiveTab('docs');
  }

  async function moveBatchDocument(index: number, delta: -1 | 1) {
    const target = index + delta;
    if (target < 0 || target >= batchDocuments.length || batchBusy) return;
    const nextItems = [...batchDocuments];
    [nextItems[index], nextItems[target]] = [nextItems[target], nextItems[index]];
    await updateBatchDocuments(nextItems, 'Document order updated.');
  }

  async function removeBatchDocument(id: string) {
    if (batchBusy) return;
    await updateBatchDocuments(batchDocuments.filter((item) => item.id !== id), 'Document removed from the print job.');
  }

  async function updateBatchDocuments(items: BatchDocumentItem[], successMessage: string) {
    setBatchBusy(true);
    setMessage('Updating combined PDF...');
    try {
      await applyBatchDocuments(items, duplex && startEachDocumentOnFront);
      setMessage(successMessage);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Could not update the combined PDF.');
    } finally {
      setBatchBusy(false);
    }
  }

  async function changeDuplex(value: string) {
    const nextDuplex = value === 'Back to back';
    setDuplex(nextDuplex);
    if (batchDocuments.length > 1 && startEachDocumentOnFront) {
      setBatchBusy(true);
      try {
        await applyBatchDocuments(batchDocuments, nextDuplex);
      } catch (error) {
        setMessage(error instanceof Error ? error.message : 'Could not update the combined PDF.');
      } finally {
        setBatchBusy(false);
      }
    }
  }

  async function changeStartEachDocumentOnFront(checked: boolean) {
    setStartEachDocumentOnFront(checked);
    if (batchDocuments.length < 2 || !duplex) return;
    setBatchBusy(true);
    setMessage('Updating page order...');
    try {
      await applyBatchDocuments(batchDocuments, checked);
      setMessage(checked ? 'Each document will start on a new front side.' : 'Documents will print continuously.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Could not update the combined PDF.');
    } finally {
      setBatchBusy(false);
    }
  }

  function openLibraryPicker(folder?: string) {
    if (folder && folder !== 'All') setNewFolderName(folder);
    libraryFileInputRef.current?.click();
  }

  function openPhotoPicker() {
    photoInputRef.current?.click();
  }

  async function requestAppInstallation(keepWhatsAppHelp = false) {
    if (!installPrompt) {
      setInstallGuide(keepWhatsAppHelp ? 'whatsapp' : 'install');
      return;
    }

    try {
      await installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      setInstallPrompt(null);
      if (choice.outcome === 'accepted') {
        setAppInstalled(true);
        setMessage('SaralPrint was added to your phone.');
        if (!keepWhatsAppHelp) setInstallGuide(null);
      } else {
        setMessage('Installation cancelled. You can still choose the document from Files.');
      }
    } catch {
      setInstallPrompt(null);
      setInstallGuide(keepWhatsAppHelp ? 'whatsapp' : 'install');
    }
  }

  function chooseFileFromGuide() {
    setInstallGuide(null);
    openDocumentPicker();
  }

  async function reloadManagedDocuments() {
    setManagedDocuments(await loadManagedDocuments());
  }

  async function addLibraryFiles(files: FileList | null) {
    const selectedFiles = Array.from(files ?? []);
    if (!selectedFiles.length) return;

    const folder = cleanFolderName(newFolderName) || defaultDocumentFolder;
    let savedCount = 0;
    for (const selectedFile of selectedFiles) {
      const file = normalizeDocumentFile(selectedFile);
      if (!isSupportedDocument(file) || file.size > maxUploadBytes) continue;
      await saveManagedDocument(file, folder);
      savedCount += 1;
    }

    await reloadManagedDocuments();
    setFolderFilter(folder);
    setMessage(savedCount ? `${savedCount} document${savedCount > 1 ? 's' : ''} saved in ${folder}.` : 'No supported documents were saved.');
    if (libraryFileInputRef.current) libraryFileInputRef.current.value = '';
  }

  async function useManagedDocument(item: ManagedDocument, selectedImageLayout: ImageLayout = 'Full page', selectedImageRotation: ImageRotation = 0) {
    if (document) URL.revokeObjectURL(document.url);
    const file = normalizeDocumentFile(new File([item.blob], item.name, { type: item.type || 'application/octet-stream' }));
    let detectedPages = 1;
    try {
      detectedPages = await detectDocumentPages(file);
      setPages(detectedPages);
      setDocumentPageCount(detectedPages);
      setPageRange('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Could not read this document.');
      return;
    }
    setDocument({ file, url: URL.createObjectURL(file) });
    setBatchDocuments(isWordDocument(file) ? [] : [{ id: createBatchDocumentId(), file, pages: detectedPages }]);
    setStartEachDocumentOnFront(false);
    setCopies(1);
    setImageLayout(selectedImageLayout);
    setImageRotation(selectedImageRotation);
    setOrder(null);
    setActiveTab('home');
    setStep(hasVerifiedShop ? 'review' : 'connect');
    setMessage(`${item.name} selected for printing.`);
  }

  async function addManagedDocumentToPrint(item: ManagedDocument) {
    const file = normalizeDocumentFile(new File([item.blob], item.name, { type: item.type || 'application/octet-stream' }));
    await selectDocument([file], batchDocuments.length ? 'append' : 'replace');
  }

  async function shareManagedDocument(item: ManagedDocument) {
    const file = new File([item.blob], item.name, { type: item.type || 'application/octet-stream' });

    try {
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          title: item.name,
          text: 'Shared from Saral Print',
          files: [file],
        });
        return;
      }

      if (navigator.share) {
        await navigator.share({
          title: item.name,
          text: `${item.name} is saved in Saral Print. Open Saral Print to print or manage it.`,
        });
        return;
      }

      downloadManagedDocument(item);
      setMessage('Sharing is unavailable in this browser, so the document was downloaded instead.');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      setMessage(error instanceof Error ? error.message : 'Could not share this document.');
    }
  }

  function persistDocumentFolders(folders: string[]) {
    const normalized = Array.from(new Set(folders.map(cleanFolderName).filter(Boolean)))
      .sort((left, right) => left.localeCompare(right));
    setCustomFolders(normalized);
    saveDocumentFolders(normalized);
  }

  function createDocumentFolder() {
    const folder = cleanFolderName(folderNameDraft);
    if (!folder) {
      setMessage('Enter a folder name.');
      return;
    }
    const existing = folderNames.find((name) => name.toLowerCase() === folder.toLowerCase());
    if (existing && existing !== 'All') {
      setFolderFilter(existing);
      setNewFolderName(existing);
      setFolderNameDraft('');
      setMessage(`${existing} already exists.`);
      return;
    }
    persistDocumentFolders([...customFolders, folder]);
    setFolderFilter(folder);
    setNewFolderName(folder);
    setFolderNameDraft('');
    setMessage(`${folder} folder created.`);
  }

  async function renameDocumentFolder(folder: string) {
    if (folder === 'All' || folder === defaultDocumentFolder) {
      setMessage(`${folder} is a system folder and cannot be renamed.`);
      return;
    }
    const requested = window.prompt('Rename folder', folder);
    if (requested === null) return;
    const nextFolder = cleanFolderName(requested);
    if (!nextFolder) {
      setMessage('Enter a folder name.');
      return;
    }
    const duplicate = folderNames.find((name) => name !== folder && name.toLowerCase() === nextFolder.toLowerCase());
    if (duplicate) {
      setMessage(`${duplicate} already exists.`);
      return;
    }
    await updateManagedDocumentFolder(folder, nextFolder);
    persistDocumentFolders([...customFolders.filter((name) => name !== folder), nextFolder]);
    await reloadManagedDocuments();
    setFolderFilter(nextFolder);
    if (newFolderName === folder) setNewFolderName(nextFolder);
    setMessage(`${folder} renamed to ${nextFolder}.`);
  }

  async function deleteDocumentFolder(folder: string) {
    if (folder === 'All' || folder === defaultDocumentFolder) {
      setMessage(`${folder} is a system folder and cannot be deleted.`);
      return;
    }
    const documentCount = managedDocuments.filter((item) => item.folder === folder).length;
    const detail = documentCount
      ? ` ${documentCount} document${documentCount === 1 ? '' : 's'} will be moved to ${defaultDocumentFolder}.`
      : '';
    if (!window.confirm(`Delete the ${folder} folder?${detail}`)) return;
    await updateManagedDocumentFolder(folder, defaultDocumentFolder);
    persistDocumentFolders(customFolders.filter((name) => name !== folder));
    await reloadManagedDocuments();
    setFolderFilter(defaultDocumentFolder);
    if (newFolderName === folder) setNewFolderName(defaultDocumentFolder);
    setMessage(`${folder} deleted.${documentCount ? ` Documents moved to ${defaultDocumentFolder}.` : ''}`);
  }

  function openDocumentOrganizer(item: ManagedDocument) {
    setEditingDocument(item);
    setDocumentNameDraft(item.name);
    setDocumentFolderDraft(item.folder || defaultDocumentFolder);
  }

  async function saveDocumentOrganization() {
    if (!editingDocument) return;
    const name = cleanDocumentName(documentNameDraft, editingDocument.name);
    const folder = cleanFolderName(documentFolderDraft) || defaultDocumentFolder;
    if (!name) {
      setMessage('Enter a document name.');
      return;
    }
    await updateManagedDocument(editingDocument.id, { name, folder });
    await reloadManagedDocuments();
    setEditingDocument(null);
    setMessage(`${name} saved in ${folder}.`);
  }

  async function deleteManagedDocument(id: string) {
    const item = managedDocuments.find((candidate) => candidate.id === id);
    if (!window.confirm(`Delete ${item?.name || 'this document'} from this device?`)) return;
    if (previewDocument?.managedDocument?.id === id) closeDocumentPreview();
    await deleteManagedDocumentById(id);
    await reloadManagedDocuments();
    if (editingDocument?.id === id) setEditingDocument(null);
    setMessage('Document removed from this device.');
  }

  function openManagedDocumentPreview(item: ManagedDocument) {
    closeDocumentPreview();
    setImageLayout('Full page');
    setImageRotation(0);
    setPreviewDocument({
      name: item.name,
      type: item.type,
      size: item.size,
      url: URL.createObjectURL(item.blob),
      revokeOnClose: true,
      managedDocument: item,
    });
  }

  function openSelectedDocumentPreview() {
    if (!document) return;
    closeDocumentPreview();
    setPreviewDocument({
      name: document.file.name,
      type: document.file.type,
      size: document.file.size,
      url: document.url,
      revokeOnClose: false,
    });
  }

  async function openFinalPagePreview() {
    if (!document) return;
    if (document.file.type === 'application/pdf' && pageRangeSelection.error) {
      setMessage(pageRangeSelection.error);
      return;
    }

    closeDocumentPreview();
    setPreviewBusy(true);
    try {
      if (document.file.type === 'application/pdf' && pageRange.trim()) {
        const { PDFDocument } = await import('pdf-lib');
        const sourcePdf = await PDFDocument.load(await document.file.arrayBuffer(), {
          ignoreEncryption: false,
          updateMetadata: false,
        });
        const selectedPdf = await PDFDocument.create();
        const copiedPages = await selectedPdf.copyPages(
          sourcePdf,
          pageRangeSelection.pages.map((page) => page - 1),
        );
        copiedPages.forEach((page) => selectedPdf.addPage(page));
        const selectedBytes = await selectedPdf.save();
        const previewBlob = new Blob([Uint8Array.from(selectedBytes).buffer], { type: 'application/pdf' });
        setPreviewDocument({
          name: document.file.name,
          type: document.file.type,
          size: previewBlob.size,
          url: URL.createObjectURL(previewBlob),
          revokeOnClose: true,
          finalPageSetup: true,
        });
        return;
      }

      setPreviewDocument({
        name: document.file.name,
        type: document.file.type,
        size: document.file.size,
        url: document.url,
        revokeOnClose: false,
        finalPageSetup: true,
      });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Could not prepare the selected pages for preview.');
    } finally {
      setPreviewBusy(false);
    }
  }

  function rotateImage(delta: -90 | 90) {
    setImageRotation((current) => ((current + delta + 360) % 360) as ImageRotation);
  }

  function closeDocumentPreview() {
    setPreviewDocument((current) => {
      if (current?.revokeOnClose) URL.revokeObjectURL(current.url);
      return null;
    });
  }

  function downloadManagedDocument(item: ManagedDocument) {
    const url = URL.createObjectURL(item.blob);
    const anchor = window.document.createElement('a');
    anchor.href = url;
    anchor.download = item.name;
    anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  async function uploadOrder() {
    if (!document) {
      openDocumentPicker();
      return;
    }

    const nextShopId = cleanShop(shopId);
    if (!nextShopId || !hasVerifiedShop) {
      setMessage('Select the shop before uploading.');
      setStep('connect');
      return;
    }
    const confirmingConvertedPdf = order?.status === 'CONVERSION_READY' && document.file.type === 'application/pdf';
    if (!isWordDocument(document.file) && customerPhoneRequired && !paymentPhoneValid) {
      setMessage(`Enter a valid 10-digit Indian mobile number for ${customerPhoneGatewayName}.`);
      return;
    }

    setBusy(true);
    setMessage('Preparing document...');

    try {
      const shop = await loadShop(nextShopId);
      setSelectedShop(shop);
      if (!isShopOpenNow(shop, Date.now())) throw new Error(shopClosedMessage(shop));
      if (shop.agentAvailable !== true) throw new Error(noActiveAgentMessage);

      if (confirmingConvertedPdf && order?.accessToken) {
        const response = await fetch(`${apiBase}/orders/${encodeURIComponent(order.orderId)}/confirm-conversion`, {
          method: 'POST',
          headers: { 'content-type': 'application/json', 'x-order-token': order.accessToken },
          body: JSON.stringify({
            shopId: nextShopId,
            copies,
            mode: mode === 'bw' ? 'Black & white' : 'Color',
            paperSize,
            orientation,
            duplex,
            pageRange: pageRangeSelection.canonical,
            customerPhone,
            paymentPreference: selectedPaymentPreference,
          }),
        });
        const body = await response.json();
        if (!response.ok) throw new Error(body.error || 'Could not confirm the converted PDF.');
        rememberOrder({ ...body, accessToken: order.accessToken });
        setStep('payment');
        setMessage(paymentNextStepMessage(body.paymentMode, 'PDF confirmed.'));
        return;
      }

      const originalFile = document.file;
      const optimizedFile = await optimizeImageForUpload(originalFile, paperSize, imageLayout, (status) => setMessage(status));
      const uploadFile = optimizedFile.type.startsWith('image/')
        ? await convertImageToPrintPdf(optimizedFile, paperSize, orientation, imageLayout, imageRotation)
        : optimizedFile;
      const optimizationMessage = optimizedFile.size < originalFile.size
        ? ` Image optimized from ${formatBytes(originalFile.size)} to ${formatBytes(optimizedFile.size)}.`
        : originalFile.type.startsWith('image/')
          ? ' Image prepared as a print-ready PDF.'
        : '';
      if (uploadFile.size > maxUploadBytes) {
        throw new Error('Document is still larger than 15 MB after optimization. Choose a smaller file.');
      }

      setMessage('Uploading document...');
      const idToken = customerUser ? await customerUser.getIdToken() : '';
      const authHeaders: Record<string, string> = idToken ? { authorization: `Bearer ${idToken}` } : {};
      const reservationResponse = await fetch(`${apiBase}/orders/uploads`, {
        method: 'POST',
        headers: { ...authHeaders, 'content-type': 'application/json' },
        body: JSON.stringify({
          shopId: nextShopId,
          fileName: uploadFile.name,
          contentType: uploadFile.type,
          fileSize: uploadFile.size,
        }),
      });
      const reservation = await reservationResponse.json();
      if (!reservationResponse.ok) throw new Error(reservation.error || 'Could not reserve document upload.');

      const uploadResponse = await fetch(reservation.uploadUrl, {
        method: 'PUT',
        headers: { 'content-type': uploadFile.type },
        body: uploadFile,
      });
      if (!uploadResponse.ok) {
        const uploadError = await uploadResponse.json().catch(() => ({}));
        throw new Error(uploadError.error || 'Document upload failed.');
      }

      const response = await fetch(`${apiBase}/orders/${encodeURIComponent(reservation.orderId)}/finalize`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'x-order-token': reservation.accessToken,
        },
        body: JSON.stringify({
          shopId: nextShopId,
          copies,
          mode: mode === 'bw' ? 'Black & white' : 'Color',
          paperSize,
          orientation: originalFile.type.startsWith('image/') ? 'Auto' : orientation,
          duplex,
          imageLayout: 'Fit',
          imageRotation: 0,
          pageRange: document.file.type === 'application/pdf' ? pageRangeSelection.canonical : '',
          customerPhone,
          paymentPreference: selectedPaymentPreference,
        }),
      });

      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Upload failed.');
      // The cloud deliberately does not return this bearer token. Keep the
      // upload-reservation token locally so returning from payment can still
      // refresh, cancel, and confirm collection for this order.
      rememberOrder({ ...body, accessToken: reservation.accessToken });
      if (['CONVERSION_PENDING', 'CONVERTING'].includes(body.status)) {
        setStep('review');
        setMessage(`Word document uploaded.${optimizationMessage} The shop computer is preparing your PDF preview.`);
      } else {
        setStep('payment');
        setMessage(paymentNextStepMessage(body.paymentMode, `Document uploaded.${optimizationMessage}`));
      }
    } catch (error) {
      setMessage(error instanceof TypeError
        ? 'Upload could not reach cloud. Check internet and try again.'
        : error instanceof Error ? error.message : 'Upload failed.');
    } finally {
      setBusy(false);
    }
  }

  async function requestPaymentLink(selectedOrder: CloudOrder) {
    if (!selectedOrder.accessToken) {
      setMessage('This order is missing its secure access token. Create a new order and try again.');
      return;
    }

    if (busy) return;
    setBusy(true);
    setMessage('Checking payment options...');
    try {
      const shop = await loadShop(selectedOrder.shopId);
      setSelectedShop(shop);
      if (shop.agentAvailable !== true) throw new Error(noActiveAgentMessage);
      const response = await fetch(`${apiBase}/orders/${selectedOrder.orderId}/payment-link`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'x-order-token': selectedOrder.accessToken,
        },
        body: JSON.stringify({
          shopId: selectedOrder.shopId,
        }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Payment link request failed.');
      rememberOrder({ ...body, accessToken: selectedOrder.accessToken });
      setMessage(body.paymentSession?.checkoutUrl
        ? 'Payment link ready. Tap Pay now and choose your UPI app.'
        : 'Payment options are being prepared. This screen will refresh automatically.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Payment link request failed.');
    } finally {
      setBusy(false);
    }
  }

  async function confirmPaymentPin(selectedOrder: CloudOrder) {
    if (!selectedOrder.accessToken) {
      setMessage('This order is missing its secure access token. Create a new order and try again.');
      return;
    }
    if (!/^\d{6}$/.test(paymentPin)) {
      setMessage('Enter the 6-digit PIN shown on the shopkeeper portal.');
      return;
    }
    try {
      setPinBusy(true);
      setMessage(demoPinOrder ? 'Checking Demo PIN…' : 'Checking Counter PIN…');
      const shop = await loadShop(selectedOrder.shopId);
      setSelectedShop(shop);
      if (shop.agentAvailable !== true) throw new Error(noActiveAgentMessage);
      const response = await fetch(`${apiBase}/orders/${encodeURIComponent(selectedOrder.orderId)}/payment-pin`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'x-order-token': selectedOrder.accessToken },
        body: JSON.stringify({ shopId: selectedOrder.shopId, pin: paymentPin }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'PIN could not be verified.');
      rememberOrder({ ...body, accessToken: selectedOrder.accessToken });
      setPaymentPin('');
      setStep('tracking');
      setMessage(body.demoOrder
        ? 'Demo approved. No payment was collected; your test print is now in the queue.'
        : 'Counter payment approved. Your print is now in the queue.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'PIN could not be verified.');
    } finally {
      setPinBusy(false);
    }
  }

  async function refreshOrder(showMessage = true, selectedOrder = order) {
    if (!selectedOrder || orderRefreshInProgressRef.current) return;
    orderRefreshInProgressRef.current = true;
    try {
      const token = selectedOrder.accessToken || '';
      const response = await fetch(`${apiBase}/orders/${selectedOrder.orderId}?shopId=${encodeURIComponent(selectedOrder.shopId)}`, {
        cache: 'no-store',
        headers: token ? { 'x-order-token': token } : {},
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Status check failed.');
      const refreshedOrder = { ...body, accessToken: selectedOrder.accessToken } as CloudOrder;
      rememberOrder(refreshedOrder);
      if (
        showMessage &&
        body.paymentMode !== 'MANUAL' &&
        ['AWAITING_PAYMENT', 'PAYMENT_EXPIRED'].includes(body.status) &&
        !body.paymentSession?.checkoutUrl
      ) {
        setMessage('Payment is waiting for confirmation.');
      } else if (showMessage && body.paymentMode === 'DEMO_PIN' && body.status === 'AWAITING_PAYMENT') {
        setMessage('Enter the Demo PIN shown by the shopkeeper. No payment will be collected.');
      } else if (showMessage && body.paymentMode === 'COUNTER_PIN' && body.status === 'AWAITING_PAYMENT') {
        setMessage('Pay the shopkeeper by cash or direct UPI, then enter the Counter PIN.');
      } else if (showMessage && body.paymentMode === 'MANUAL' && body.status === 'AWAITING_PAYMENT') {
        setMessage(`Pay manually at the shop and give token ${visibleQueueToken(body)} to the shopkeeper.`);
      } else if (showMessage && body.status === 'PAID') {
        setMessage('Payment received. Your print order is being prepared.');
      } else if (showMessage) {
        setMessage(`Order update: ${statusLabel(body.status)}.`);
      }
    } catch (error) {
      if (showMessage) setMessage(error instanceof Error ? error.message : 'Status check failed.');
    } finally {
      orderRefreshInProgressRef.current = false;
    }
  }

  async function confirmCollected() {
    if (!order?.accessToken) {
      setMessage('This order is missing its secure access token.');
      return;
    }
    if (!window.confirm('Have you collected your printed documents?')) return;
    setBusy(true);
    try {
      const response = await fetch(`${apiBase}/orders/${order.orderId}/collected`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'x-order-token': order.accessToken },
        body: JSON.stringify({ shopId: order.shopId }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Collection confirmation failed.');
      rememberOrder({ ...body, accessToken: order.accessToken });
      setMessage('Collection confirmed. Thank you for using SaralPrint.');
      setActiveTab('home');
      setStep('collected');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Collection confirmation failed.');
    } finally {
      setBusy(false);
    }
  }

  async function cancelAndRestart(selectedOrder = order, restart = true) {
    if (!selectedOrder?.accessToken) {
      setMessage('This order is missing its secure access token and cannot be cancelled from this device.');
      return;
    }
    const confirmation = restart
      ? `Cancel token ${visibleQueueToken(selectedOrder)} and start again?`
      : `Cancel token ${visibleQueueToken(selectedOrder)} and remove it from this device?`;
    if (!window.confirm(confirmation)) return;

    setBusy(true);
    try {
      const response = await fetch(`${apiBase}/orders/${encodeURIComponent(selectedOrder.orderId)}/cancel`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'x-order-token': selectedOrder.accessToken },
        body: JSON.stringify({ shopId: selectedOrder.shopId }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Order cancellation failed.');
      const cancelledOrder = { ...body, accessToken: selectedOrder.accessToken } as CloudOrder;
      if (restart) {
        rememberOrder(cancelledOrder);
        reprintOrder(cancelledOrder);
      } else {
        forgetOrder(cancelledOrder);
        setOrder(null);
        setStep('home');
        setMessage('Order cancelled and removed from this device.');
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Order cancellation failed.');
    } finally {
      setBusy(false);
    }
  }

  async function loadOrderFromUrl(orderId: string, orderShopId: string, storedOrder?: CloudOrder) {
    const selectedOrder: CloudOrder = storedOrder || {
      orderId,
      shopId: orderShopId,
      fileName: '',
      checksum: '',
      shortChecksum: '',
      paymentReference: '',
      pages: 1,
      copies: 1,
      paperSize: 'A4',
      orientation: 'Auto',
      duplex: false,
      mode: '',
      amount: 0,
      status: 'PAYMENT_PENDING',
    };
    // Render tracking immediately after the hosted payment provider returns.
    // refreshOrder preserves the locally stored secret token while replacing
    // the rest of the object with authoritative cloud state.
    setOrder(selectedOrder);
    setRecentOrder(selectedOrder);
    await refreshOrder(true, selectedOrder);
  }

  function beginHostedPayment(event: React.MouseEvent<HTMLAnchorElement>) {
    const navigationWasAlreadyCancelled = event.defaultPrevented;
    event.preventDefault();
    if (gatewayHandoffRef.current) {
      return;
    }
    const targetUrl = safeCheckoutUrl(event.currentTarget.href);
    if (!targetUrl) {
      setMessage('The secure payment link is invalid. Request a new link and try again.');
      return;
    }

    gatewayHandoffRef.current = true;
    gatewayPageHiddenRef.current = false;
    setGatewayHandoffInProgress(true);
    setMessage('Checking shop availability before payment...');
    void (async () => {
      try {
        const activeShopId = order?.shopId || selectedShop?.shopId || '';
        const shop = await loadShop(activeShopId);
        setSelectedShop(shop);
        if (shop.agentAvailable !== true) throw new Error(noActiveAgentMessage);
        setMessage('Opening secure payment in this tab. You will return here automatically after payment.');
        if (!navigationWasAlreadyCancelled) window.location.assign(targetUrl);
      } catch (error) {
        gatewayHandoffRef.current = false;
        setGatewayHandoffInProgress(false);
        setMessage(error instanceof Error ? error.message : 'Shop availability could not be verified. Try again.');
      }
    })();
  }

  function clearGatewayHandoff() {
    gatewayHandoffRef.current = false;
    setGatewayHandoffInProgress(false);
  }

  function reset() {
    if (document) URL.revokeObjectURL(document.url);
    setDocument(null);
    setBatchDocuments([]);
    setBatchBusy(false);
    setStartEachDocumentOnFront(false);
    setChoosingLibraryDocuments(false);
    clearGatewayHandoff();
    setOrder(null);
    setPages(1);
    setDocumentPageCount(1);
    setPageRange('');
    setCopies(1);
    setMode('bw');
    setPaperSize('A4');
    setOrientation('Auto');
    setDuplex(false);
    setImageLayout('Full page');
    setImageRotation(0);
    setActiveTab('home');
    setStep('home');
    setMessage('');
    conversionDownloadInProgressRef.current.clear();
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function rememberOrder(nextOrder: CloudOrder) {
    const orderKey = `${nextOrder.shopId}:${nextOrder.orderId}`;
    const previousStatus = knownOrderStatusesRef.current.get(orderKey);
    knownOrderStatusesRef.current.set(orderKey, nextOrder.status);
    if (nextOrder.status === 'PRINTING' && previousStatus && previousStatus !== 'PRINTING') {
      setMessage(`Printing started for token ${visibleQueueToken(nextOrder)}.`);
    }
    setOrder(nextOrder);
    setRecentOrder(nextOrder);
    saveRecentOrder(nextOrder);
    setOrderHistory((currentHistory) => {
      const nextHistory = upsertOrderHistory(currentHistory, nextOrder);
      saveOrderHistory(nextHistory);
      return nextHistory;
    });
  }

  function trackOrder(selectedOrder: CloudOrder) {
    setShopId(selectedOrder.shopId);
    setOrder(selectedOrder);
    setActiveTab('home');
    setStep(selectedOrder.status === 'COLLECTED' ? 'collected' : selectedOrder.status === 'PRINTED' ? 'ready' : 'tracking');
    setMessage(`Viewing token ${visibleQueueToken(selectedOrder)}.`);
    void refreshOrder(false, selectedOrder);
  }

  function reprintOrder(selectedOrder: CloudOrder) {
    setShopId(selectedOrder.shopId);
    setPages(selectedOrder.pages);
    setPageRange(selectedOrder.pageRange || '');
    setCopies(selectedOrder.copies || 1);
    setPaperSize(selectedOrder.paperSize === 'Legal' ? 'Legal' : 'A4');
    setOrientation(['Portrait', 'Landscape'].includes(selectedOrder.orientation) ? selectedOrder.orientation as 'Portrait' | 'Landscape' : 'Auto');
    setDuplex(selectedOrder.duplex === true);
    setImageLayout(normalizeImageLayout(selectedOrder.imageLayout));
    setImageRotation(normalizeImageRotation(selectedOrder.imageRotation));
    setMode(selectedOrder.mode.toLowerCase().includes('color') ? 'color' : 'bw');
    setOrder(null);
    const canReuseSelectedFile = document?.file.name === selectedOrder.fileName;
    const previousOutcome = selectedOrder.cancelledBy === 'CUSTOMER' ? 'Previous order cancelled.' : 'Previous order failed.';
    if (!canReuseSelectedFile) setDocument(null);
    if (!canReuseSelectedFile) setBatchDocuments([]);
    setChoosingLibraryDocuments(false);
    setActiveTab('home');
    setStep(canReuseSelectedFile ? 'review' : 'document');
    setMessage(canReuseSelectedFile
      ? `${previousOutcome} Review the settings and create a new order.`
      : `${previousOutcome} Select the same file again to restart.`);
  }

  function openHomeUpload() {
    setActiveTab('home');
    setChoosingLibraryDocuments(false);
    setStep(document && hasVerifiedShop ? 'review' : 'document');
  }

  function openTab(tab: AppTab) {
    stopQrScanner();
    setChoosingLibraryDocuments(false);
    setActiveTab(tab);
    if (tab === 'home') setStep('home');
  }

  function goBack(): boolean {
    if (previewDocument) {
      closeDocumentPreview();
      return true;
    }
    if (scannerOpen) {
      stopQrScanner();
      setScanMessage('Camera closed. Start it again or enter the shop code.');
      return true;
    }

    stopQrScanner();
    if (activeTab !== 'home') {
      setChoosingLibraryDocuments(false);
      setActiveTab('home');
      setStep('home');
      return true;
    }
    if (step === 'payment') {
      setStep('review');
      return true;
    }
    if (step === 'review') {
      setStep('document');
      return true;
    }
    if (step === 'connect' && document) {
      setStep('document');
      return true;
    }
    if (step !== 'home') {
      setScanMessage('');
      setActiveTab('home');
      setStep('home');
      return true;
    }
    return false;
  }

  appBackActionRef.current = goBack;

  function forgetOrder(orderToForget: CloudOrder) {
    const matchesOrder = (candidate: CloudOrder | null) => Boolean(
      candidate && candidate.shopId === orderToForget.shopId && candidate.orderId === orderToForget.orderId,
    );
    knownOrderStatusesRef.current.delete(`${orderToForget.shopId}:${orderToForget.orderId}`);
    setOrderHistory((currentHistory) => {
      const nextHistory = currentHistory.filter((candidate) => !matchesOrder(candidate));
      saveOrderHistory(nextHistory);
      return nextHistory;
    });
    setRecentOrder((current) => {
      if (!matchesOrder(current)) return current;
      clearRecentOrder();
      return null;
    });
  }

  function openConnectShop() {
    setScanMessage('');
    setActiveTab('home');
    setStep('connect');
  }

  function renderHeader(title: string, subtitle = '', showBack = false) {
    const isBrandHeader = !showBack && title === 'Saral Print';
    return (
      <>
      <header className={`top-bar${isBrandHeader ? ' brand-top-bar' : ''}${showBack ? ' with-back' : ''}`}>
        {showBack && (
          <button className="icon-button" type="button" aria-label="Go back" onClick={goBack}>
            <Icon name="arrow-left" />
          </button>
        )}
        {isBrandHeader ? (
          <>
            <a className="brand-lockup" href="#top" aria-label="SaralPrint home">
              <img src="/saral-print-icon.png?v=brand-v3" alt="" />
              <span className="brand-wordmark" aria-hidden="true">
                <span className="brand-wordmark-saral">SARAL</span>
                <span className="brand-wordmark-print">PRINT</span>
              </span>
            </a>
            {!showBack && <div className="header-actions">
              <button className="icon-button account-button" type="button" aria-label="Account and settings" onClick={() => setActiveTab('profile')}>
                {customerUser?.photoURL ? <img src={customerUser.photoURL} alt="" referrerPolicy="no-referrer" /> : <Icon name="user" />}
              </button>
              <button
                className="icon-button theme-toggle"
                type="button"
                aria-label={resolvedTheme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
                onClick={toggleTheme}
              >
                <Icon name={resolvedTheme === 'dark' ? 'sun' : 'moon'} />
              </button>
            </div>}
            {subtitle && <p className="brand-subtitle">{subtitle}</p>}
          </>
        ) : (
          <>
            <div className="title-wrap">
              <span className="title-mark">
                {showBack ? <Icon name={headerIcon(title)} /> : null}
              </span>
              <div className="title-copy">
                <strong>{title}</strong>
                {subtitle && <span>{subtitle}</span>}
              </div>
            </div>
            {!showBack && <div className="header-actions">
              <button className="icon-button account-button" type="button" aria-label="Account and settings" onClick={() => setActiveTab('profile')}>
                {customerUser?.photoURL ? <img src={customerUser.photoURL} alt="" referrerPolicy="no-referrer" /> : <Icon name="user" />}
              </button>
              <button
                className="icon-button theme-toggle"
                type="button"
                aria-label={resolvedTheme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
                onClick={toggleTheme}
              >
                <Icon name={resolvedTheme === 'dark' ? 'sun' : 'moon'} />
              </button>
            </div>}
          </>
        )}
      </header>
      {selectedShop && !['connect', 'home'].includes(step) && (
        <div className="shop-context">
          <div><Icon name="shop" /><span><strong>{selectedShop.shopName}</strong><small>{selectedShop.city || selectedShop.shopId}</small></span></div>
          <button type="button" onClick={() => setStep('connect')}>Change</button>
        </div>
      )}
      {activeTab === 'home' && ['connect', 'document', 'review', 'payment'].includes(step) && (
        <nav className="order-progress" aria-label="Print order progress">
          {([['Shop', 'connect', hasVerifiedShop], ['File', 'document', Boolean(document)], ['Options', 'review', step === 'payment'], ['Payment', 'payment', false]] as const).map(([label, stage, done]) => (
            <span key={stage} className={`${step === stage ? 'current' : ''}${done && step !== stage ? ' done' : ''}`} aria-current={step === stage ? 'step' : undefined}>{done && step !== stage ? '✓ ' : ''}{label}</span>
          ))}
        </nav>
      )}
      </>
    );
  }

  function renderHome() {
    if (selectedShop) {
      return (
        <>
          <header className="simple-home-brand">
            <a className="simple-home-brand-lockup" href="#top" aria-label="SaralPrint home">
              <img src="/saral-print-icon.png?v=brand-v3" alt="" />
              <span><strong><span>SARAL</span> PRINT</strong><small>Upload. Pay. Collect.</small></span>
            </a>
          </header>
          <section className="simple-shop-card" aria-label="Selected print shop">
            <span className="simple-shop-icon"><Icon name="pin" /></span>
            <div>
              <small>Printing at</small>
              <strong>{selectedShop.shopName}</strong>
              {(selectedShop.address || selectedShop.city) && <span className="simple-shop-address">{[selectedShop.address, selectedShop.city].filter(Boolean).join(', ')}</span>}
              <ShopRateSummary shop={selectedShop} compact />
              <span className={`simple-shop-availability ${shopOpenNow && agentAvailable ? 'online' : 'offline'}`}>{shopOpenNow ? agentAvailable ? 'Printer online · accepting orders' : 'Printer offline' : selectedShop.printingPaused ? 'Shop paused' : 'Shop closed'}</span>
            </div>
            <button type="button" onClick={() => setStep('connect')}><span>Change</span><Icon name="chevron-right" /></button>
          </section>
          {renderPrintToolCards()}
        </>
      );
    }
    return (
      <>
        {renderHeader('Saral Print', 'Print nearby in minutes')}
        {renderPrintToolCards()}
        <section className="start-flow-card">
          <div><small>ALREADY AT THE COUNTER?</small><h2>Connect to your print shop</h2><p>{recentShops.length ? 'Choose a recent shop or scan the QR at the counter.' : 'Scan the shop QR to see its rates and send your order.'}</p></div>
          {renderRecentShops()}
          <button className="scan-button" type="button" onClick={() => setStep('connect')}><Icon name="qr" /><span>Scan shop QR</span></button>
        </section>

        <section className="screen-section">
          <SectionHeader title="Recent Orders" action="View all" onAction={() => openTab('orders')} />
          <div className="compact-list">
            {orderHistory.length ? orderHistory.slice(0, 2).map((item) => (
              <OrderListItem key={`${item.shopId}-${item.orderId}`} order={item} onTrack={() => trackOrder(item)} />
            )) : <EmptyState title="No orders yet" description="Your orders will appear here after you print." />}
          </div>
        </section>
      </>
    );
  }

  function renderPrintToolCards() {
    return <section className="home-tool-section" aria-label="Start a print order">
      <div className="home-tool-heading"><small>YOUR PRINT STARTS HERE</small><h1>What do you have ready?</h1><p>Choose a PDF, Word document or photo. You will see the shop price before placing the order.</p></div>
      <button type="button" className="home-upload-action" aria-label="Upload document to print" onClick={openHomeUpload}>
        <span className="home-upload-icon"><Icon name="upload" /></span>
        <span className="home-upload-copy"><strong>Print a file</strong><small>PDF, Word, photo or a file from WhatsApp</small></span>
        <span className="home-upload-arrow"><Icon name="chevron-right" /></span>
      </button>
      <p className="home-tool-privacy"><Icon name="lock" /> Your file is sent to the shop only after you confirm the order.</p>
    </section>;
  }

  function renderConnect() {
    return (
      <>
        {renderHeader('Select Print Shop', '', true)}
        <section className="single-screen">
          {renderRecentShops()}
          <p className="screen-intro">Scan the shop QR or enter the code shown at the counter.</p>
          <div className="camera-panel">
            {scannerOpen ? <video ref={videoRef} muted playsInline /> : <button type="button" onClick={startQrScanner}><Icon name="qr" /><span>Start camera</span></button>}
          </div>
          {scanMessage && <p className="hint center-text">{scanMessage}</p>}
          <div className="divider-label">OR ENTER CODE</div>
          <input className="large-input" value={shopId} onChange={(event) => setShopId(event.target.value.toUpperCase())} autoCapitalize="characters" spellCheck={false} placeholder="ABC123" aria-label="Six-character shop code" />
          <button className="primary-button fixed-cta" type="button" onClick={confirmShop} disabled={shopChecking}>
            <Icon name={shopChecking ? 'clock' : 'shop'} /><span>{shopChecking ? 'Checking...' : 'Connect Shop'}</span>
          </button>
        </section>
      </>
    );
  }

  function renderDocumentPicker() {
    return (
      <>
        {renderHeader('Select Documents', '', true)}
        <section className="single-screen">
          <section className="document-source-picker" aria-labelledby="document-source-title">
            <div><strong id="document-source-title">Where is your document?</strong><small>Choose the place that is easiest for you.</small></div>
            <div className="document-source-options">
              <button className="document-source-option whatsapp-source" type="button" onClick={() => setInstallGuide('whatsapp')}>
                <span className="document-source-icon"><Icon name="share" /></span>
                <span><strong>WhatsApp</strong><small>Share or select a received document</small></span>
                <Icon name="chevron-right" />
              </button>
              <button className="document-source-option" type="button" onClick={openDocumentPicker}>
                <span className="document-source-icon"><Icon name="folder" /></span>
                <span><strong>Files / Downloads</strong><small>PDF, Word document or image</small></span>
                <Icon name="chevron-right" />
              </button>
              <button className="document-source-option" type="button" onClick={openPhotoPicker}>
                <span className="document-source-icon"><Icon name="eye" /></span>
                <span><strong>Photos</strong><small>Select a photo or screenshot</small></span>
                <Icon name="chevron-right" />
              </button>
            </div>
            <button className="secondary-button wide" type="button" onClick={openSavedDocumentPicker}><Icon name="folder" /><span>From My Documents</span></button>
          </section>
          <section className="screen-section flush">
            <h2>Recently saved</h2>
            <div className="compact-list">
              {recentDocuments.length ? recentDocuments.map((item) => (
                <button className="list-row" type="button" key={item.id} onClick={() => useManagedDocument(item)}>
                  <FileMark />
                  <div><strong>{item.name}</strong><small>{formatBytes(item.size)}</small></div>
                </button>
              )) : <p className="empty-copy">No saved files on this device.</p>}
            </div>
          </section>
        </section>
      </>
    );
  }

  function renderPrintOptions() {
    const pageRangeInvalid = document?.file.type === 'application/pdf' && Boolean(pageRangeSelection.error);
    const wordDocument = document ? isWordDocument(document.file) : false;
    if (document && wordDocument) {
      const conversionAvailable = selectedShop?.wordConversionAvailable === true;
      return (
        <>
          {renderHeader('Convert Word to PDF', '', true)}
          <section className="single-screen options-screen">
            <button className="document-card" type="button" onClick={openSelectedDocumentPreview}>
              <span className="document-card-main"><FileMark type={document.file.type} /><strong>{document.file.name}</strong></span>
              <span>{formatBytes(document.file.size)}</span>
            </button>
            <WordDocumentPreview pages={documentPageCount} paperSize={paperSize} orientation={orientation} />
            <div className="wait-card">
              <strong>{wordConversionInProgress ? 'Preparing your PDF preview' : 'Convert before choosing print settings'}</strong>
              <span>{wordConversionInProgress
                ? 'Microsoft Word on the selected shop computer is preserving the document layout and creating a PDF. This screen refreshes automatically.'
                : 'The PDF page count and preview will be confirmed before pricing or payment. The converted PDF will also be saved in Docs for reuse.'}</span>
            </div>
            {!shopOpenNow && <div className="status-alert" role="status"><Icon name="clock" /><span>{shopClosedMessage(selectedShop)}</span></div>}
            {!conversionAvailable && !wordConversionInProgress && <div className="status-alert error" role="alert"><Icon name="clock" /><span>The selected shop agent is offline or Microsoft Word is unavailable. Choose another shop or upload a PDF.</span></div>}
            {wordConversionInProgress
              ? <><button className="primary-button fixed-cta" type="button" disabled><Icon name="clock" /><span>Converting to PDF...</span></button>{order && <button className="secondary-button danger wide" type="button" disabled={busy} onClick={() => cancelAndRestart(order)}><Icon name="trash" /><span>Cancel conversion</span></button>}</>
              : <button className={`primary-button fixed-cta${!shopOpenNow ? ' shop-closed-cta' : ''}`} type="button" disabled={!hasVerifiedShop || !shopOpenNow || !conversionAvailable || busy} onClick={uploadOrder}><Icon name={busy ? 'clock' : 'refresh'} /><span>{busy ? 'Uploading Word document...' : shopOpenNow ? 'Convert to PDF & preview' : selectedShop?.printingPaused ? 'Shop is paused' : 'Shop is closed'}</span></button>}
          </section>
        </>
      );
    }
    return (
      <>
        {renderHeader('Print Options', '', true)}
        <section className="single-screen options-screen">
          {document && (
            <button className="document-card" type="button" onClick={openSelectedDocumentPreview}>
              <span className="document-card-main"><FileMark type={document.file.type} /><strong>{document.file.name}</strong></span>
              <span>{pages} page{pages > 1 ? 's' : ''} - {formatBytes(document.file.size)}</span>
            </button>
          )}
          {batchDocuments.length === 1 && (
            <section className="batch-add-sources" aria-label="Add another document">
              <strong>Add another document</strong>
              <div>
                <button className="secondary-button" type="button" disabled={batchBusy} onClick={openAdditionalDocumentPicker}><Icon name="upload" /><span>From phone</span></button>
                <button className="secondary-button" type="button" disabled={batchBusy} onClick={openSavedDocumentPicker}><Icon name="folder" /><span>From My Documents</span></button>
              </div>
            </section>
          )}
          {batchDocuments.length > 1 && (
            <section className="batch-documents" aria-labelledby="batch-documents-title">
              <div className="batch-documents-heading">
                <span><strong id="batch-documents-title">Documents</strong><small>Printed in this order</small></span>
              </div>
              <ol className="batch-document-list">
                {batchDocuments.map((item, index) => (
                  <li key={item.id}>
                    <span className="batch-document-number">{index + 1}</span>
                    <span className="batch-document-copy"><strong>{item.file.name}</strong><small>{item.pages} page{item.pages === 1 ? '' : 's'} · {formatBytes(item.file.size)}</small></span>
                    <span className="batch-document-actions">
                      <button type="button" aria-label={`Move ${item.file.name} up`} disabled={batchBusy || index === 0} onClick={() => void moveBatchDocument(index, -1)}>↑</button>
                      <button type="button" aria-label={`Move ${item.file.name} down`} disabled={batchBusy || index === batchDocuments.length - 1} onClick={() => void moveBatchDocument(index, 1)}>↓</button>
                      <button className="danger" type="button" aria-label={`Remove ${item.file.name}`} disabled={batchBusy} onClick={() => void removeBatchDocument(item.id)}><Icon name="trash" /></button>
                    </span>
                  </li>
                ))}
              </ol>
              {batchDocuments.length < 10 && <div className="batch-add-sources compact" aria-label="Add documents">
                <div>
                  <button className="secondary-button" type="button" disabled={batchBusy} onClick={openAdditionalDocumentPicker}><Icon name="upload" /><span>From phone</span></button>
                  <button className="secondary-button" type="button" disabled={batchBusy} onClick={openSavedDocumentPicker}><Icon name="folder" /><span>From My Documents</span></button>
                </div>
              </div>}
              {batchBusy && <p className="batch-document-status"><Icon name="clock" /> Updating combined PDF...</p>}
            </section>
          )}
          {document?.file.type === 'application/pdf' && documentPageCount > 1 ? (
            <label className={`page-range-field${pageRangeInvalid ? ' invalid' : ''}`}>
              <span>Pages</span>
              <input
                value={pageRange}
                inputMode="text"
                maxLength={2000}
                placeholder={selectedPageLabel}
                aria-invalid={pageRangeInvalid}
                aria-describedby="page-range-help"
                onChange={(event) => {
                  const nextRange = event.target.value;
                  setPageRange(nextRange);
                  const selection = parsePageRange(nextRange, documentPageCount);
                  setPages(selection.pages.length);
                }}
              />
              <small id="page-range-help">
                {pageRangeSelection.error || (pageRange.trim()
                  ? `${pageRangeSelection.pages.length} of ${documentPageCount} pages selected (${pageRangeSelection.canonical})`
                  : `Leave blank to print ${selectedPageLabel.toLowerCase()}. Example: 1-5, 8, 9`)}
              </small>
            </label>
          ) : <SummaryLine icon="file" label="Number of pages" value={String(pages)} />}
          <Stepper label="Copies" value={copies} onMinus={() => setCopies(Math.max(1, copies - 1))} onPlus={() => setCopies(Math.min(100, copies + 1))} />
          <OptionGroup
            title="Print"
            options={printModeOptions}
            value={mode === 'bw' ? 'B/W' : 'Colour'}
            disabledOptions={colorPrintingEnabled ? [] : ['Colour']}
            hint={colorPrintingEnabled ? '' : 'This shop has not enabled colour printing.'}
            onSelect={(value) => setMode(value === 'Colour' ? 'color' : 'bw')}
          />
          {paperSizeOptions.length > 1 && <OptionGroup title="Paper" options={paperSizeOptions} value={paperSize} onSelect={(value) => setPaperSize(value as 'A4' | 'Legal')} />}
          <details className="advanced-print-settings">
            <summary><span>More print settings</span><small>{orientation} · {duplex ? 'Back to back' : 'Single-sided'}{document?.file.type.startsWith('image/') ? ` · ${imageLayout} · ${imageRotation}°` : ''}</small></summary>
            <div className="advanced-print-settings-body">
              <OptionGroup title="Orientation" options={['Auto', 'Portrait', 'Landscape']} value={orientation} onSelect={(value) => setOrientation(value as 'Auto' | 'Portrait' | 'Landscape')} />
              <OptionGroup title="Sides" options={['Single-sided', 'Back to back']} value={duplex ? 'Back to back' : 'Single-sided'} onSelect={(value) => void changeDuplex(value)} />
              {batchDocuments.length > 1 && duplex && (
                <label className="front-side-option">
                  <input type="checkbox" checked={startEachDocumentOnFront} disabled={batchBusy} onChange={(event) => void changeStartEachDocumentOnFront(event.target.checked)} />
                  <span><strong>Start each document on a front side</strong><small>Blank pages are added only when needed and included in the page total.</small></span>
                </label>
              )}
              {document?.file.type.startsWith('image/') && <OptionGroup title="Image size" options={imageLayoutOptions} value={imageLayout} onSelect={(value) => setImageLayout(value as ImageLayout)} />}
              {document?.file.type.startsWith('image/') && <ImageRotationControls rotation={imageRotation} onRotate={rotateImage} />}
            </div>
          </details>
          <div className="print-summary">
            <span>{pages} page{pages === 1 ? '' : 's'} × {copies} cop{copies === 1 ? 'y' : 'ies'} · {mode === 'bw' ? 'B/W' : 'Colour'} · {paperSize} · {orientation} · {duplex ? 'Back to back' : 'Single-sided'}{document?.file.type.startsWith('image/') ? ` · ${imageLayout} · ${imageRotation}°` : ''}</span>
            <strong>{hasVerifiedShop ? <>Total {formatCurrency(amount)}</> : 'Select a shop to see the price'}</strong>
            <small>{mode === 'bw' ? 'B/W' : 'Colour'} {paperSize} · {formatCurrency(rate)} per page · {pages * copies} printed side{pages * copies === 1 ? '' : 's'}</small>
            {document?.file.type.startsWith('image/') && document.file.size > imageOptimizationThresholdBytes && <small>{imageLayout === 'Original size' ? 'Original image preserved for accurate physical size.' : 'Large image will be optimized for a faster upload without reducing print resolution.'}</small>}
          </div>
          <button className="secondary-button wide final-preview-button" type="button" disabled={previewBusy || batchBusy || pageRangeInvalid} onClick={() => void openFinalPagePreview()}><Icon name={previewBusy || batchBusy ? 'clock' : 'eye'} /><span>{previewBusy || batchBusy ? 'Preparing preview...' : batchDocuments.length > 1 ? 'Preview combined PDF' : 'Preview selected pages'}</span></button>
          {!shopOpenNow && <div className="status-alert" role="status"><Icon name="clock" /><span>{shopClosedMessage(selectedShop)}</span></div>}
          {!agentAvailable && <div className="status-alert error" role="alert"><Icon name="printer" /><span>{noActiveAgentMessage}</span></div>}
          {!paymentAvailable && <div className="status-alert error" role="status"><Icon name="credit-card" /><span>Payments are not enabled for this shop. Ask the shopkeeper to connect Razorpay, enable pay at counter, or start a PIN session.</span></div>}
          <button className={`primary-button fixed-cta${!shopOpenNow ? ' shop-closed-cta' : ''}`} type="button" disabled={!hasVerifiedShop || !shopOpenNow || !agentAvailable || !paymentAvailable || pageRangeInvalid || batchBusy || busy} onClick={() => setStep('payment')}><Icon name="credit-card" /><span>{shopOpenNow ? (!agentAvailable ? 'No print agent active' : paymentAvailable ? (manualPaymentShop ? 'Continue to approval' : 'Continue to payment') : 'Payment setup required') : selectedShop?.printingPaused ? 'Shop is paused' : 'Shop is closed'}</span></button>
        </section>
      </>
    );
  }

  function renderPayment() {
    const confirmingConvertedPdf = order?.status === 'CONVERSION_READY';
    return (
      <>
        {renderHeader(order?.paymentMode === 'MANUAL' || (!order && selectedPaymentPreference === 'manual') ? 'Pay at counter' : (pinPaymentOrder || manualPaymentShop)
          ? ((demoPinOrder || (!order && shopCheckoutMode === 'demo_pin')) ? 'Demo approval' : 'Counter approval')
          : manualPaymentOrder ? 'Pay at shop' : 'Razorpay payment', '', true)}
        <section className="single-screen payment-screen">
          <div className="payment-total-card">
            <span><Icon name="credit-card" />Total payable</span>
            <strong>{formatCurrency(order?.amount || amount)}</strong>
          </div>
          <details className="payment-details">
            <summary>
              <span><Icon name="file" />Order details</span>
              <small>{compactFileName(document?.file.name || order?.fileName || 'Selected document')} · {order?.pages || pages} page{(order?.pages || pages) === 1 ? '' : 's'}</small>
            </summary>
            <div className="payment-details-body">
              <SummaryLine icon="shop" label="Print shop" value={selectedShop?.shopName || 'Selected print shop'} />
              <SummaryLine icon="file" label="Document" value={compactFileName(document?.file.name || order?.fileName || 'Selected document')} />
              <SummaryLine icon="orders" label="Pages and copies" value={`${order?.pageRange ? `${order.pageRange} (${order.pages})` : order?.pages || pages} × ${order?.copies || copies}`} />
              <SummaryLine icon="printer" label="Print settings" value={`${(order?.mode || mode).toLowerCase().includes('color') ? 'Colour' : 'B/W'} · ${order?.paperSize || paperSize} · ${order?.orientation || orientation} · ${(order?.duplex ?? duplex) ? 'Back to back' : 'Single-sided'}${document?.file.type.startsWith('image/') ? ` · ${order?.imageLayout || imageLayout} · ${normalizeImageRotation(order?.imageRotation ?? imageRotation)}°` : ''}`} />
            </div>
          </details>
          {!order && manualPaymentChoice && <fieldset className="payment-method-choice"><legend>How would you like to pay?</legend>{shopCheckoutMode === 'razorpay' && <label><input type="radio" name="paymentPreference" checked={selectedPaymentPreference === 'online'} onChange={() => setPaymentPreference('online')} /><span><strong>Pay online</strong><small>UPI, card or other Razorpay options</small></span></label>}<label><input type="radio" name="paymentPreference" checked={selectedPaymentPreference === 'manual'} onChange={() => setPaymentPreference('manual')} /><span><strong>Pay at counter</strong><small>Cash or direct payment; shopkeeper approves printing</small></span></label></fieldset>}
          {!agentAvailable && <div className="status-alert error" role="alert"><Icon name="printer" /><span>{noActiveAgentMessage}</span></div>}
          {(!order || confirmingConvertedPdf) && customerPhoneRequired && <label className={`payment-phone-field ${customerPhone && !paymentPhoneValid ? 'invalid' : ''}`}>
            <span>Mobile number</span>
            <input
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              maxLength={10}
              value={customerPhone}
              onChange={(event) => {
                const digits = event.target.value.replace(/\D/g, '');
                setCustomerPhone(digits.length > 10 && digits.startsWith('91') ? digits.slice(2, 12) : digits.slice(0, 10));
              }}
              placeholder="10-digit mobile number"
            />
            <small>Required by {customerPhoneGatewayName}. We save it on this device to make your next order faster.</small>
            {!paymentPhoneValid && <small className="payment-validation">Enter your 10-digit mobile number to continue. Your number is shared only with this print shop and {customerPhoneGatewayName}.</small>}
          </label>}
          {!order || confirmingConvertedPdf ? (
            <>{!shopOpenNow && <div className="status-alert" role="status"><Icon name="clock" /><span>{shopClosedMessage(selectedShop)}</span></div>}<button className={`primary-button fixed-cta${!shopOpenNow ? ' shop-closed-cta' : ''}`} type="button" disabled={!hasVerifiedShop || !shopOpenNow || !agentAvailable || !document || (customerPhoneRequired && !paymentPhoneValid) || busy} onClick={uploadOrder}><Icon name={busy ? 'clock' : 'orders'} /><span>{busy ? 'Confirming PDF...' : shopOpenNow ? (!agentAvailable ? 'No print agent active' : `${confirmingConvertedPdf ? 'Confirm PDF' : 'Send print order'} · ${formatCurrency(amount)}`) : selectedShop?.printingPaused ? 'Shop is paused' : 'Shop is closed'}</span></button></>
          ) : !agentAvailable && paymentNeedsAction ? (
            <div className="wait-card"><strong>No print agent active</strong><span>Payment and printing will remain unavailable until the shopkeeper activates an Android or Windows agent.</span></div>
          ) : pinPaymentOrder ? (
            <div className={`pin-approval-card ${demoPinOrder ? 'demo' : 'counter'}`}>
              <strong>{demoPinOrder ? 'Demo mode · no payment' : 'Pay the shopkeeper first'}</strong>
              <span>{demoPinOrder
                ? 'Ask the shopkeeper for the running Demo PIN shown on their portal.'
                : 'Pay by cash or direct UPI, then ask the shopkeeper for the running Counter PIN.'}</span>
              <label>
                <span>6-digit shop PIN</span>
                <input type="password" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={paymentPin} onChange={(event) => setPaymentPin(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="••••••" aria-label="6-digit shop PIN" />
              </label>
              <button className="primary-button wide" type="button" disabled={pinBusy || paymentPin.length !== 6} onClick={() => void confirmPaymentPin(order)}><Icon name={pinBusy ? 'clock' : 'check'} /><span>{pinBusy ? 'Checking PIN…' : demoPinOrder ? 'Approve demo print' : 'Confirm counter payment'}</span></button>
              <small>{demoPinOrder ? `The displayed price is ${formatCurrency(order.amount)}, but this demo collects ₹0.` : `SaralPrint does not verify cash or direct UPI. By entering the shop PIN, you confirm ${formatCurrency(order.amount)} was handled at the counter.`}</small>
            </div>
          ) : manualPaymentOrder ? (
            <div className="wait-card">
              <strong>Pay manually at the shop</strong>
              <span>Give this token to the shopkeeper, pay at the counter, and the shopkeeper will confirm the payment before printing.</span>
              <strong className="queue-token">{visibleQueueToken(order)}</strong>
            </div>
          ) : paymentConfirmationPending ? (
            <PaymentConfirmationWait seconds={paymentConfirmationSeconds} />
          ) : !shopOpenNow ? (
            <div className="wait-card"><strong>{selectedShop?.printingPaused ? 'Shop is paused' : 'Shop is closed'}</strong><span>{shopClosedMessage(selectedShop)}</span></div>
          ) : checkoutUrl ? (
            <>{gatewayHandoffInProgress
              ? <button className="primary-button wide fixed-cta" type="button" disabled><Icon name="clock" /><span>Opening secure payment...</span></button>
              : <a className="primary-button wide fixed-cta" href={checkoutUrl} onClick={beginHostedPayment}><Icon name="credit-card" /><span>Pay securely · {formatCurrency(order.amount || amount)}</span></a>}<p className="gateway-handoff">Secure payment powered by {gatewayDisplayName(order.paymentSession?.provider)}. You will return to Saral Print after payment.</p></>
          ) : (
            <div className="wait-card">
              <strong>Preparing payment options</strong>
              <span>Please wait a moment. This screen refreshes automatically.</span>
              <button className="primary-button wide" type="button" disabled={busy || paymentPreparationSeconds > 0} onClick={() => requestPaymentLink(order)}><Icon name={busy ? 'clock' : 'refresh'} /><span>{busy ? 'Checking...' : paymentPreparationSeconds > 0 ? `Checking automatically · ${paymentPreparationSeconds}s` : 'Still waiting? Check again'}</span></button>
            </div>
          )}
          {order && <PrintStatusTimeline order={order} compact />}
          {preparingPaymentLink && paymentPreparationSeconds > 0 && <PaymentPreparationProgress seconds={paymentPreparationSeconds} />}
          {order && canCustomerCancel(order.status) && <button className="secondary-button danger wide" type="button" disabled={busy || gatewayHandoffInProgress} onClick={() => cancelAndRestart(order)}><Icon name="trash" /><span>Cancel &amp; Start Again</span></button>}
          <p className="secure-copy">{pinPaymentOrder ? (demoPinOrder ? 'Demo orders are clearly marked and excluded from collected payment.' : 'Counter payment happens outside SaralPrint; the PIN securely releases this order.') : manualPaymentOrder || (!order && selectedPaymentPreference === 'manual') ? 'Your order will be printed after the shopkeeper confirms your counter payment.' : 'Payment is completed on Razorpay’s secure hosted page. Your order enters the print queue only after Razorpay confirms it.'}</p>
          {!order && <p className="collection-eta">Usually ready 5–10 minutes after payment confirmation.</p>}
        </section>
        {gatewayHandoffInProgress && <GatewayHandoffProgress />}
      </>
    );
  }

  function renderTracking() {
    if (!order) return renderHome();
    const exceptionMessage = trackingExceptionMessage(order);
    return (
      <>
        {renderHeader('Order Tracking', '', true)}
        <section className="single-screen tracking-screen">
          <div className="order-hero">
            <span className="hero-mark"><Icon name="printer" /></span>
            <small>Your queue token</small>
            <strong className="queue-token">{visibleQueueToken(order)}</strong>
            <span>{statusLabel(order.status)} · {order.pageRange ? `pages ${order.pageRange} (${order.pages})` : `${order.pages} page${order.pages === 1 ? '' : 's'}`} × {order.copies || 1}</span>
            <span>{selectedShop?.shopName || 'Selected print shop'}</span>
          </div>
          <PrintStatusTimeline order={order} />
          {exceptionMessage && order.status !== 'FAILED' && <div className={`status-alert ${order.status === 'PAYMENT_EXPIRED' ? 'error' : ''}`} role="status"><Icon name={order.status === 'PAYMENT_EXPIRED' ? 'clock' : 'credit-card'} /><span>{exceptionMessage}</span></div>}
          {order.status === 'FAILED' && <div className="failure-card" role="alert"><strong>{order.cancelledBy === 'CUSTOMER' ? 'Order cancelled' : 'Order failed'}</strong><span>{order.statusMessage || 'This order could not continue. You can start again safely.'}</span></div>}
          {order.status !== 'FAILED' && <div className="eta-card"><span><Icon name="clock" />Estimated Time</span><strong>{['PRINTED', 'COLLECTED'].includes(order.status) ? 'Ready' : '2 mins'}</strong></div>}
          {!agentAvailable && paymentNeedsAction && <div className="status-alert error" role="alert"><Icon name="printer" /><span>{noActiveAgentMessage}</span></div>}
          {agentAvailable && pinPaymentOrder && paymentNeedsAction && <div className="pin-approval-card compact"><strong>{demoPinOrder ? 'Enter Demo PIN · no payment' : 'Pay at counter, then enter PIN'}</strong><input type="password" inputMode="numeric" maxLength={6} value={paymentPin} onChange={(event) => setPaymentPin(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="6-digit PIN" aria-label="6-digit shop PIN" /><button className="primary-button wide" type="button" disabled={pinBusy || paymentPin.length !== 6} onClick={() => void confirmPaymentPin(order)}><Icon name={pinBusy ? 'clock' : 'check'} /><span>{pinBusy ? 'Checking…' : 'Release print order'}</span></button></div>}
          {agentAvailable && !pinPaymentOrder && manualPaymentOrder && paymentNeedsAction && <div className="status-alert" role="status"><Icon name="orders" /><span>Pay manually at the counter and give token {visibleQueueToken(order)} to the shopkeeper. They will confirm payment in the Android or Windows agent and approve printing.</span></div>}
          {!shopOpenNow && paymentNeedsAction && <div className="status-alert" role="status"><Icon name="clock" /><span>{shopClosedMessage(selectedShop)}</span></div>}
          {paymentConfirmationPending && <PaymentConfirmationWait seconds={paymentConfirmationSeconds} compact />}
          {!gatewayHandoffInProgress && agentAvailable && !manualPaymentOrder && shopOpenNow && paymentNeedsAction && paymentLinkReady && checkoutUrl && <a className="primary-button fixed-cta" href={checkoutUrl} onClick={beginHostedPayment}><Icon name="credit-card" /><span>Pay securely</span></a>}
          {!gatewayHandoffInProgress && agentAvailable && !manualPaymentOrder && shopOpenNow && paymentNeedsAction && !paymentLinkReady && <button className="primary-button wide fixed-cta" type="button" disabled={busy} onClick={() => requestPaymentLink(order)}><Icon name={busy ? 'clock' : 'refresh'} /><span>{busy ? 'Requesting...' : 'Request payment link'}</span></button>}
          <button className="secondary-button wide" type="button" onClick={() => refreshOrder(true)}><Icon name="refresh" /><span>Refresh Status</span></button>
          {canCustomerCancel(order.status) && <button className="secondary-button danger wide" type="button" disabled={busy} onClick={() => cancelAndRestart(order)}><Icon name="trash" /><span>Cancel &amp; Start Again</span></button>}
          {order.status === 'FAILED' && <button className="primary-button wide fixed-cta" type="button" onClick={() => reprintOrder(order)}><Icon name="refresh" /><span>Start Again</span></button>}
        </section>
      </>
    );
  }

  function renderReady() {
    if (!order) return renderHome();
    return (
      <>
        <section className="ready-screen">
          <div className="success-mark"><Icon name="check" /><span>READY</span></div>
          <h1>Your print is ready</h1>
          <p>{compactFileName(order.fileName)}</p>
          <div className="order-card"><strong className="queue-token">{visibleQueueToken(order)}</strong><span>Show this token when collecting</span></div>
          <PrintStatusTimeline order={order} />
          <button className="primary-button success-action wide fixed-cta" type="button" disabled={busy} onClick={confirmCollected}><Icon name="check" /><span>{busy ? 'Confirming...' : 'I have collected my print'}</span></button>
          <button className="secondary-button wide" type="button" onClick={() => reprintOrder(order)}><Icon name="printer" /><span>Print Again</span></button>
        </section>
      </>
    );
  }

  function renderCollected() {
    if (!order) return renderHome();
    return (
      <>
        <section className="ready-screen">
          <div className="success-mark"><Icon name="check" /><span>COLLECTED</span></div>
          <h1>Print collected</h1>
          <p>Thank you for using SaralPrint.</p>
          <div className="order-card"><strong className="queue-token">{visibleQueueToken(order)}</strong><span>{compactFileName(order.fileName)}</span></div>
          <PrintStatusTimeline order={order} />
          {!appInstalled && !installOfferDismissed && <section className="install-promotion-card" aria-label="Add SaralPrint to your phone">
            <span className="install-promotion-icon"><Icon name="upload" /></span>
            <div><strong>Print faster next time</strong><p>Add SaralPrint to your phone, then share documents directly from WhatsApp on supported Android phones.</p></div>
            <button className="primary-button wide" type="button" onClick={() => void requestAppInstallation(false)}><Icon name="upload" /><span>Add SaralPrint to phone</span></button>
            <button className="text-action" type="button" onClick={() => setInstallOfferDismissed(true)}>Not now</button>
          </section>}
          <button className="primary-button success-action wide fixed-cta" type="button" onClick={() => { setActiveTab('home'); setOrder(null); setStep('home'); setMessage(''); }}><Icon name="home" /><span>Done · Back to Home</span></button>
          <button className="secondary-button wide" type="button" onClick={() => { setActiveTab('orders'); setStep('home'); }}><Icon name="orders" /><span>View my orders</span></button>
          <button className="secondary-button wide" type="button" onClick={() => reprintOrder(order)}><Icon name="printer" /><span>Print Again</span></button>
        </section>
      </>
    );
  }

  function renderDocs() {
    const libraryActionLabel = choosingLibraryDocuments
      ? (batchDocuments.length ? 'Add to print job' : 'Select for printing')
      : 'Print';
    return (
      <>
        {renderHeader(choosingLibraryDocuments ? 'Add Saved Documents' : 'My Documents', choosingLibraryDocuments ? 'Add files to this print job' : `${managedDocuments.length} saved`, true)}
        <section className="screen-section documents-screen">
          <div className="document-library-tools">
            <button className="primary-button compact-add-button" type="button" onClick={() => openLibraryPicker(folderFilter)}><Icon name="upload" /><span>Upload document</span></button>
            <label className="folder-select"><span>Save in</span><select value={newFolderName} onChange={(event) => setNewFolderName(event.target.value)}>{uploadFolderNames.map((folder) => <option key={folder} value={folder}>{folder}</option>)}</select></label>
          </div>
          <div className="folder-create-row">
            <label className="folder-name-field"><span>New folder</span><input value={folderNameDraft} maxLength={32} placeholder="e.g. Certificates" onChange={(event) => setFolderNameDraft(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') createDocumentFolder(); }} /></label>
            <button className="secondary-button" type="button" onClick={createDocumentFolder}><Icon name="folder" /><span>Create</span></button>
          </div>
          <p className="document-share-help">{choosingLibraryDocuments
            ? 'Tap Add on every document you want to include. You can arrange the combined job before printing.'
            : 'Documents stay on this device. Use Organize to rename a file or move it between folders.'}</p>
          {choosingLibraryDocuments && <button className="primary-button wide" type="button" disabled={!batchDocuments.length || batchBusy} onClick={() => { setChoosingLibraryDocuments(false); setActiveTab('home'); setStep('review'); }}><Icon name="check" /><span>Done adding · {batchDocuments.length} document{batchDocuments.length === 1 ? '' : 's'}</span></button>}
          <div className="folder-tabs">
            {folderNames.map((folder) => <button className={folderFilter === folder ? 'selected' : ''} type="button" key={folder} onClick={() => setFolderFilter(folder)}>{folder}</button>)}
          </div>
          {folderFilter !== 'All' && <div className="folder-actions">
            <strong>{folderFilter}</strong>
            {folderFilter !== defaultDocumentFolder && <><button type="button" onClick={() => void renameDocumentFolder(folderFilter)}><Icon name="pencil" /><span>Rename folder</span></button><button className="danger" type="button" onClick={() => void deleteDocumentFolder(folderFilter)}><Icon name="trash" /><span>Delete folder</span></button></>}
          </div>}
          <div className="document-list">
            {visibleManagedDocuments.length ? visibleManagedDocuments.map((item) => (
              <article className="managed-document" key={item.id}>
                <button className="managed-document-head" type="button" onClick={() => openManagedDocumentPreview(item)} aria-label={`Preview ${item.name}`}>
                  <FileMark type={item.type} />
                  <span className="managed-document-copy"><strong>{item.name}</strong><span>{item.folder} · {formatBytes(item.size)} · {formatDocumentDate(item.createdAt)}</span></span>
                  <span className="preview-hint"><Icon name="eye" /><span>Preview</span></span>
                </button>
                <div className="document-actions">
                  <button className="primary-button" type="button" disabled={batchBusy} onClick={() => void (choosingLibraryDocuments ? addManagedDocumentToPrint(item) : useManagedDocument(item))}><Icon name={choosingLibraryDocuments ? 'check' : 'printer'} /><span>{libraryActionLabel}</span></button>
                  {choosingLibraryDocuments
                    ? <button className="secondary-button" type="button" onClick={() => openManagedDocumentPreview(item)}><Icon name="eye" /><span>Preview</span></button>
                    : <button className="secondary-button" type="button" onClick={() => openDocumentOrganizer(item)}><Icon name="pencil" /><span>Organize</span></button>}
                </div>
              </article>
            )) : <EmptyState title="No documents yet" action="Upload" onAction={() => openLibraryPicker(folderFilter)} />}
          </div>
          {visibleManagedDocuments.length > 0 && folderFilter !== 'All' && (
            <button className="secondary-button wide" type="button" onClick={() => openLibraryPicker(folderFilter)}>
              <Icon name="upload" /><span>Upload another document</span>
            </button>
          )}
        </section>
      </>
    );
  }

  function renderOrders() {
    return (
      <>
        {renderHeader('My Orders', '', true)}
        <section className="screen-section">
          <OrderGroup title="Current orders" orders={activeOrders} action="View order" onOrder={trackOrder} />
          <OrderGroup title="Ready" orders={readyOrders} action="Collect" onOrder={trackOrder} />
          <OrderGroup title="Completed" orders={completedOrders} action="Reorder" onOrder={reprintOrder} />
          <OrderGroup title="Failed" orders={failedOrders} action="Retry" onOrder={reprintOrder} />
          {!orderHistory.length && <EmptyState title="No orders yet" action="Upload document" onAction={openHomeUpload} />}
        </section>
      </>
    );
  }

  function renderProfile() {
    return (
      <>
        {renderHeader('Profile', '', true)}
        <section className="screen-section">
          {customerUser ? (
            <div className="customer-account-card">
              {customerUser.photoURL ? <img src={customerUser.photoURL} alt="" referrerPolicy="no-referrer" /> : <span className="customer-avatar"><Icon name="user" /></span>}
              <div><small>CUSTOMER ACCOUNT</small><strong>{customerUser.displayName || 'SaralPrint customer'}</strong><span>{customerUser.email}</span></div>
              <button className="secondary-button" type="button" onClick={logoutCustomer}>Sign out</button>
              {customerProfile && <div className="persona-list" aria-label="Account personas">
                <span>Customer</span>
                {customerProfile.personas.shopkeeper && <span>Shopkeeper</span>}
                {customerProfile.personas.admin && <span>Administrator</span>}
                {customerProfile.personas.tester && <span>Tester</span>}
              </div>}
            </div>
          ) : (
            <div className="customer-login-card">
              <span className="customer-avatar"><Icon name="user" /></span>
              <div><strong>Sign in optionally</strong><p>Sync features will be added progressively. You can always scan, upload and print without signing in.</p></div>
              <button className="primary-button wide" type="button" disabled={authBusy} onClick={loginCustomer}><Icon name="user" /><span>{authBusy ? 'Opening Google...' : 'Continue with Google'}</span></button>
              <small>First-time users are registered automatically as customers.</small>
            </div>
          )}
          {authError && <p className="auth-error">{authError}</p>}
          {!hasFirebaseConfig && <p className="auth-error">Firebase customer sign-in is unavailable in this build.</p>}
          <ThemeSwitcher preference={themePreference} resolved={resolvedTheme} onChange={setTheme} />
          {!appInstalled && <section className="profile-install-card">
            <span className="install-promotion-icon"><Icon name="upload" /></span>
            <div><strong>Add SaralPrint to your phone</strong><small>Open it like an app and share documents directly on supported Android phones.</small></div>
            <button className="secondary-button wide" type="button" onClick={() => void requestAppInstallation(false)}><Icon name="upload" /><span>Install SaralPrint</span></button>
          </section>}
          <div className="shop-card"><strong><Icon name="shop" />Current shop</strong><span>{selectedShop?.shopName || selectedShopId || 'No shop connected'}</span></div>
          <button className="primary-button" type="button" onClick={openConnectShop}><Icon name="shop" /><span>{selectedShop ? 'Change Shop' : 'Connect Shop'}</span></button>
          <button className="secondary-button wide" type="button" onClick={reset}><Icon name="printer" /><span>Start New Print</span></button>
        </section>
      </>
    );
  }

  function renderCurrentScreen() {
    if (activeTab === 'docs') return renderDocs();
    if (activeTab === 'orders') return renderOrders();
    if (activeTab === 'profile') return renderProfile();
    if (step === 'connect') return renderConnect();
    if (step === 'document') return renderDocumentPicker();
    if (step === 'review') return renderPrintOptions();
    if (step === 'payment') return renderPayment();
    if (step === 'tracking') return renderTracking();
    if (step === 'ready') return renderReady();
    if (step === 'collected') return renderCollected();
    return renderHome();
  }

  return (
    <main className={`app shell ${hideBottomNavigation ? 'without-bottom-nav' : ''}`}>
      {showSplash && <SplashScreen onComplete={() => {
        try {
          window.sessionStorage.setItem(startupSplashStorageKey, 'true');
        } catch {
          // Storage can be unavailable in private browsing; the splash still closes.
        }
        setShowSplash(false);
      }} />}
      <input ref={fileInputRef} hidden multiple type="file" accept="application/pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg,image/jpg,image/webp" onChange={(event) => selectDocument(event.target.files)} />
      <input ref={libraryFileInputRef} hidden multiple type="file" accept="application/pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg,image/jpg,image/webp" onChange={(event) => addLibraryFiles(event.target.files)} />
      <input ref={photoInputRef} hidden multiple type="file" accept="image/png,image/jpeg,image/jpg,image/webp" onChange={(event) => selectDocument(event.target.files)} />
      <div className="content-area">{renderCurrentScreen()}</div>
      <div className="message-bar" role="status" aria-live="polite" aria-atomic="true">{message}</div>
      {!hideBottomNavigation && <BottomNav active={activeTab} onChange={openTab} pendingPaymentCount={pendingPaymentCount} />}
      {showWordConversionProgress && order && (
        <WordConversionProgress
          seconds={wordConversionElapsedSeconds}
          status={order.status}
          busy={busy}
          onCancel={() => void cancelAndRestart(order)}
        />
      )}
      {installGuide && <Modal title={installGuide === 'whatsapp' ? 'Use a document from WhatsApp' : 'Add SaralPrint to your phone'} className="install-guide-modal" onClose={() => setInstallGuide(null)}>
        {installGuide === 'whatsapp' ? (
          isIosDevice() ? <div className="install-guide-content">
            <div className="install-guide-lead"><span className="whatsapp-mark"><Icon name="share" /></span><div><strong>On iPhone, select the downloaded file</strong><p>WhatsApp cannot send a document directly to this web app. This quick method works without installing anything.</p></div></div>
            <ol className="install-steps"><li><b>1</b><span>Open the document in WhatsApp and wait for it to download.</span></li><li><b>2</b><span>Return to SaralPrint and tap <strong>Choose recent file</strong>.</span></li><li><b>3</b><span>Select the document from Recents or Downloads.</span></li></ol>
            <button className="primary-button wide" type="button" onClick={chooseFileFromGuide}><Icon name="folder" /><span>Choose recent file</span></button>
          </div> : appInstalled ? <div className="install-guide-content">
            <div className="install-guide-lead"><span className="whatsapp-mark"><Icon name="check" /></span><div><strong>SaralPrint is ready for WhatsApp</strong><p>Use the Android Share menu to send the document into SaralPrint.</p></div></div>
            <ol className="install-steps"><li><b>1</b><span>Return to WhatsApp and open the PDF, Word document or image.</span></li><li><b>2</b><span>Tap <strong>Share</strong>.</span></li><li><b>3</b><span>Choose <strong>SaralPrint</strong>.</span></li></ol>
            <button className="secondary-button wide" type="button" onClick={chooseFileFromGuide}><Icon name="folder" /><span>Choose from Files instead</span></button>
          </div> : <div className="install-guide-content">
            <div className="install-guide-lead"><span className="whatsapp-mark"><Icon name="share" /></span><div><strong>Install once to share from WhatsApp</strong><p>After installation, SaralPrint can appear in Android’s Share menu.</p></div></div>
            <button className="primary-button wide" type="button" disabled={!installPrompt} onClick={() => void requestAppInstallation(true)}><Icon name={installPrompt ? 'upload' : 'clock'} /><span>{installPrompt ? 'Install SaralPrint' : 'Preparing install button…'}</span></button>
            {!installPrompt && <div className="install-wait-copy" role="status"><Icon name="clock" /><span>Keep this page open in Chrome. The button activates automatically as soon as Chrome finishes checking the app.</span></div>}
            {!installPrompt && <details className="install-manual-fallback"><summary>Button is not becoming active?</summary><p>Make sure this page is open in Chrome and not inside WhatsApp’s mini browser. As a fallback, Chrome’s menu also contains Install app or Add to Home screen.</p></details>}
            <div className="install-guide-divider"><span>or continue without installing</span></div>
            <button className="secondary-button wide" type="button" onClick={chooseFileFromGuide}><Icon name="folder" /><span>Choose downloaded document</span></button>
          </div>
        ) : (
          <div className="install-guide-content">
            <div className="install-guide-lead"><span className="install-promotion-icon"><Icon name="upload" /></span><div><strong>{isIosDevice() ? 'Add from Safari' : 'Install from your browser'}</strong><p>{isIosDevice() ? 'Apple requires these steps to add a web app.' : 'Your browser has not offered the automatic install window yet.'}</p></div></div>
            {isIosDevice() ? <ol className="install-steps"><li><b>1</b><span>Open this page in <strong>Safari</strong>.</span></li><li><b>2</b><span>Tap <strong>Share</strong>, then <strong>Add to Home Screen</strong>.</span></li><li><b>3</b><span>Turn on <strong>Open as Web App</strong> and tap Add.</span></li></ol> : <><button className="primary-button wide" type="button" disabled={!installPrompt} onClick={() => void requestAppInstallation(false)}><Icon name={installPrompt ? 'upload' : 'clock'} /><span>{installPrompt ? 'Install SaralPrint' : 'Preparing install button…'}</span></button>{!installPrompt && <div className="install-wait-copy" role="status"><Icon name="clock" /><span>Keep this page open in Chrome. Installation becomes available automatically after Chrome verifies the app.</span></div>}</>}
            <button className="secondary-button wide" type="button" onClick={() => setInstallGuide(null)}>Continue in browser</button>
          </div>
        )}
      </Modal>}
      {editingDocument && (
        <Modal title="Organize document" className="document-organizer-modal" onClose={() => setEditingDocument(null)}>
          <form className="document-organizer-form" onSubmit={(event) => { event.preventDefault(); void saveDocumentOrganization(); }}>
            <label><span>Document name</span><input autoFocus value={documentNameDraft} maxLength={100} onChange={(event) => setDocumentNameDraft(event.target.value)} /></label>
            <label><span>Folder</span><select value={documentFolderDraft} onChange={(event) => setDocumentFolderDraft(event.target.value)}>{uploadFolderNames.map((folder) => <option key={folder} value={folder}>{folder}</option>)}</select></label>
            <p>The file extension is kept automatically when you rename the document.</p>
            <div className="document-organizer-actions">
              <button className="primary-button" type="submit"><Icon name="check" /><span>Save changes</span></button>
              <button className="secondary-button" type="button" onClick={() => void shareManagedDocument(editingDocument)}><Icon name="share" /><span>Share</span></button>
              <button className="secondary-button danger" type="button" onClick={() => void deleteManagedDocument(editingDocument.id)}><Icon name="trash" /><span>Delete document</span></button>
            </div>
          </form>
        </Modal>
      )}
      {previewDocument && (
        <Modal title={`${previewDocument.finalPageSetup ? 'Final page' : 'Preview'} · ${compactFileName(previewDocument.name)}`} className={`document-preview-modal${previewDocument.finalPageSetup ? ' final-page-modal' : ''}`} onClose={closeDocumentPreview}>
          <div className="preview-file-meta"><FileMark type={previewDocument.type} /><span><strong>{previewDocument.name}</strong><small>{formatBytes(previewDocument.size)}</small></span></div>
          {previewDocument.finalPageSetup ? <>
            {previewDocument.type.startsWith('image/')
              ? <FinalImagePagePreview url={previewDocument.url} layout={imageLayout} rotation={imageRotation} paperSize={paperSize} orientation={orientation} mode={mode} />
              : previewDocument.type === 'application/pdf'
                ? <FinalPdfPagePreview url={previewDocument.url} paperSize={paperSize} orientation={orientation} mode={mode} pageLabel={pageRangeSelection.canonical ? `Pages ${pageRangeSelection.canonical}` : `All ${documentPageCount} pages`} totalPages={pages} />
                : <WordDocumentPreview pages={documentPageCount} paperSize={paperSize} orientation={orientation} />}
            {previewDocument.type.startsWith('image/') && <ImageRotationControls rotation={imageRotation} onRotate={rotateImage} compact />}
            <p className="preview-help">{previewDocument.type === 'application/pdf'
              ? documentPageCount > 1
                ? 'This is the page sent to the printer. Use Previous and Next to check the other selected pages.'
                : 'This is the single page that will print.'
              : previewDocument.type.startsWith('image/')
                ? `This preview matches shop print placement on ${paperSize} (${imageLayout}). Only a few millimetres of printer hardware margin may differ.`
                : `Microsoft Word will convert this macro-free DOCX to PDF on the shop agent. Printing stops safely if Word repaginates it to a different page count.`}</p>
          </> : previewDocument.type.startsWith('image/') ? <>
            <div className="preview-layout-options" aria-label="Image layout">
              {imageLayoutOptions.map((layout) => <button className={imageLayout === layout ? 'selected' : ''} type="button" key={layout} onClick={() => setImageLayout(layout)}>{layout}</button>)}
            </div>
            <ImageRotationControls rotation={imageRotation} onRotate={rotateImage} compact />
            <div className={`image-preview-stage ${imageLayoutClass(imageLayout)}`}><img src={previewDocument.url} alt={previewDocument.name} style={{ transform: `rotate(${imageRotation}deg)` }} /></div>
            <p className="preview-help">{imageLayoutHelp(imageLayout)}</p>
          </> : previewDocument.type === 'application/pdf'
            ? <div className="pdf-preview"><object className="pdf-frame" data={`${previewDocument.url}#view=FitH`} type="application/pdf"><strong>PDF preview is unavailable in this browser.</strong><a className="secondary-button" href={previewDocument.url} target="_blank" rel="noreferrer">Open PDF</a></object></div>
            : <WordDocumentPreview pages={documentPageCount} paperSize={paperSize} orientation={orientation} />}
          {(!previewDocument.finalPageSetup || previewDocument.managedDocument) && <div className="preview-actions">
            {previewDocument.managedDocument && <button className="primary-button" type="button" onClick={() => { const item = previewDocument.managedDocument!; const layout = imageLayout; const rotation = imageRotation; closeDocumentPreview(); if (choosingLibraryDocuments) void addManagedDocumentToPrint(item); else void useManagedDocument(item, layout, rotation); }}><Icon name={choosingLibraryDocuments ? 'check' : 'printer'} /><span>{choosingLibraryDocuments ? 'Add to print job' : 'Print this document'}</span></button>}
            {!previewDocument.finalPageSetup && <button className="secondary-button" type="button" onClick={closeDocumentPreview}>Close preview</button>}
          </div>}
        </Modal>
      )}
    </main>
  );
}

function customerAuthError(error: unknown) {
  const message = error instanceof Error ? error.message : String(error || 'Google sign-in failed.');
  if (message.includes('auth/unauthorized-domain')) return `This domain is not authorized in Firebase: ${window.location.hostname}`;
  if (message.includes('auth/popup-closed-by-user')) return 'Google sign-in was closed before completion.';
  if (message.includes('auth/operation-not-allowed')) return 'Google sign-in is not enabled in Firebase.';
  return 'Google sign-in failed. Please try again.';
}

function isStandaloneApp() {
  const navigatorWithStandalone = navigator as Navigator & { standalone?: boolean };
  return window.matchMedia('(display-mode: standalone)').matches || navigatorWithStandalone.standalone === true;
}

function isIosDevice() {
  const appleMobile = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const iPadDesktopMode = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return appleMobile || iPadDesktopMode;
}

function Icon({ name }: { name: IconName }) {
  const paths: Record<IconName, React.ReactNode> = {
    'arrow-left': <path d="M15 6 9 12l6 6M10 12h10" />,
    check: <path d="m5 12 4 4L19 6" />,
    'chevron-right': <path d="m9 18 6-6-6-6" />,
    clock: <><circle cx="12" cy="12" r="8" /><path d="M12 8v5l3 2" /></>,
    'credit-card': <><rect x="3" y="6" width="18" height="12" rx="2" /><path d="M3 10h18M7 15h3" /></>,
    eye: <><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" /><circle cx="12" cy="12" r="2.5" /></>,
    file: <><path d="M7 3h7l4 4v14H7z" /><path d="M14 3v5h5M9 13h6M9 17h4" /></>,
    folder: <><path d="M3 7h7l2 2h9v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><path d="M3 7V6a2 2 0 0 1 2-2h4l2 3" /></>,
    home: <><path d="m3 11 9-7 9 7" /><path d="M5 10v10h14V10M10 20v-6h4v6" /></>,
    lock: <><rect x="5" y="10" width="14" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></>,
    menu: <><path d="M4 7h16M4 12h16M4 17h16" /></>,
    moon: <><path d="M21 14.5A8.5 8.5 0 0 1 9.5 3 7 7 0 1 0 21 14.5Z" /></>,
    orders: <><path d="M7 4h10v16H7z" /><path d="M9 8h6M9 12h6M9 16h4" /></>,
    pencil: <><path d="m4 20 4.1-1 10-10a2.1 2.1 0 0 0-3-3l-10 10z" /><path d="m13.5 7.5 3 3" /></>,
    pin: <><path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z" /><circle cx="12" cy="10" r="2.5" /></>,
    printer: <><path d="M7 8V3h10v5" /><path d="M6 17H5a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-1" /><path d="M7 14h10v7H7z" /></>,
    qr: <><path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4z" /><path d="M14 14h2v2h-2zM18 14h2v6h-4v-2M14 18v2" /></>,
    refresh: <><path d="M20 6v5h-5" /><path d="M4 18v-5h5" /><path d="M18.5 9A7 7 0 0 0 6 7.5M5.5 15A7 7 0 0 0 18 16.5" /></>,
    share: <><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="m8.6 10.6 6.8-4.2M8.6 13.4l6.8 4.2" /></>,
    shop: <><path d="M4 10h16l-2-6H6z" /><path d="M5 10v10h14V10M9 20v-6h6v6" /></>,
    sun: <><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></>,
    trash: <><path d="M4 7h16M10 11v6M14 11v6M6 7l1 14h10l1-14M9 7V4h6v3" /></>,
    upload: <><path d="M12 16V4" /><path d="m7 9 5-5 5 5" /><path d="M5 20h14" /></>,
    user: <><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>,
  };

  return (
    <svg className="ui-icon" viewBox="0 0 24 24" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}

function FileMark({ type = '' }: { type?: string }) {
  const label = type === 'application/pdf' ? 'PDF' : type.startsWith('image/') ? 'IMG' : 'DOC';
  return (
    <span className="file-mark" aria-hidden="true">
      <Icon name="file" />
      <span>{label}</span>
    </span>
  );
}

function shopPageRate(shop: PublicShop | null, paperSize: 'A4' | 'Legal', mode: 'bw' | 'color') {
  if (!shop) return 0;
  const paperRates = shop.rates?.byPaper?.[paperSize];
  if (paperRates) return paperRates[mode] ?? 0;
  return mode === 'bw' ? (shop.rates?.bw ?? 0) : (shop.rates?.color ?? 0);
}

function ShopRateSummary({ shop, compact = false }: { shop: PublicShop; compact?: boolean }) {
  const supportsColor = shop.printModes?.includes('Color') === true;
  const papers: ('A4' | 'Legal')[] = shop.paperSizes?.includes('Legal') ? ['A4', 'Legal'] : ['A4'];
  return (
    <div className={compact ? 'rate-line' : 'rate-grid'}>
      {papers.flatMap((paper) => [
        <b key={`${paper}-bw`}>{paper} B/W {formatCurrency(shopPageRate(shop, paper, 'bw'))}/page</b>,
        ...(supportsColor ? [<b key={`${paper}-color`}>{paper} Colour {formatCurrency(shopPageRate(shop, paper, 'color'))}/page</b>] : []),
      ])}
    </div>
  );
}

function headerIcon(title: string): IconName {
  if (title.includes('Shop') || title === 'Profile') return title === 'Profile' ? 'user' : 'shop';
  if (title.includes('Document') || title.includes('Docs')) return 'folder';
  if (title.includes('Print') || title.includes('Tracking')) return 'printer';
  if (title.includes('Payment')) return 'credit-card';
  if (title.includes('Orders')) return 'orders';
  return 'home';
}

function optionIcon(option: string): IconName {
  if (option === 'B/W' || option === 'Colour') return 'printer';
  if (option === 'A4' || option === 'Legal') return 'file';
  if (option === 'Auto') return 'refresh';
  return 'file';
}

function SectionHeader({ title, action, onAction }: { title: string; action?: string; onAction?: () => void }) {
  return (
    <div className="section-head">
      <h2>{title}</h2>
      {action && <button type="button" onClick={onAction}>{action}</button>}
    </div>
  );
}

function EmptyState({ title, action, onAction, description = 'Upload a document to print.' }: { title: string; action?: string; onAction?: () => void; description?: string }) {
  return (
    <div className="empty-state">
      <span className="empty-icon"><Icon name="file" /></span>
      <strong>{title}</strong>
      <span>{description}</span>
      {action && onAction && <button className="secondary-button" type="button" onClick={onAction}><Icon name="upload" /><span>{action}</span></button>}
    </div>
  );
}

function Stepper({ label, value, onMinus, onPlus }: { label: string; value: number; onMinus: () => void; onPlus: () => void }) {
  return (
    <div className="option-row">
      <span>{label}</span>
      <div className="stepper">
        <button type="button" onClick={onMinus}>-</button>
        <strong>{value}</strong>
        <button type="button" onClick={onPlus}>+</button>
      </div>
    </div>
  );
}

function OptionGroup({ title, options, value, disabledOptions = [], hint = '', onSelect }: {
  title: string;
  options: string[];
  value: string;
  disabledOptions?: string[];
  hint?: string;
  onSelect: (value: string) => void;
}) {
  return (
    <div className="option-group">
      <span>{title}</span>
      <div>
        {options.map((option) => (
          <button className={value === option ? 'selected' : ''} type="button" key={option} disabled={disabledOptions.includes(option)} onClick={() => onSelect(option)}>
            <Icon name={optionIcon(option)} />
            <span>{option}</span>
          </button>
        ))}
      </div>
      {hint && <small className="option-group-hint">{hint}</small>}
    </div>
  );
}

function SummaryLine({ icon, label, value, strong = false }: { icon: IconName; label: string; value: string; strong?: boolean }) {
  return (
    <div className="summary-line">
      <span><Icon name={icon} />{label}</span>
      {strong ? <strong>{value}</strong> : <b>{value}</b>}
    </div>
  );
}

type PageRangeSelection = {
  pages: number[];
  canonical: string;
  error: string;
};

function parsePageRange(value: string, pageCount: number): PageRangeSelection {
  const allPages = Array.from({ length: pageCount }, (_, index) => index + 1);
  const trimmed = value.trim();
  if (!trimmed) return { pages: allPages, canonical: '', error: '' };
  if (trimmed.length > 2000) return { pages: [], canonical: '', error: 'Page range is too long.' };

  const selected = new Set<number>();
  for (const rawPart of trimmed.split(',')) {
    const part = rawPart.trim();
    if (!part) return { pages: [], canonical: '', error: 'Enter pages like 1-5, 8, 9.' };
    const match = part.match(/^(\d+)\s*(?:-\s*(\d+))?$/);
    if (!match) return { pages: [], canonical: '', error: 'Enter pages like 1-5, 8, 9.' };
    const start = Number(match[1]);
    const end = Number(match[2] || match[1]);
    if (start < 1 || end < 1 || start > pageCount || end > pageCount) {
      return { pages: [], canonical: '', error: `Choose pages between 1 and ${pageCount}.` };
    }
    if (start > end) return { pages: [], canonical: '', error: `Range ${start}-${end} must go from lower to higher.` };
    for (let page = start; page <= end; page += 1) selected.add(page);
  }

  const pages = [...selected].sort((a, b) => a - b);
  return { pages, canonical: compactPageRange(pages), error: '' };
}

function compactPageRange(pages: number[]) {
  const parts: string[] = [];
  for (let index = 0; index < pages.length; index += 1) {
    const start = pages[index];
    let end = start;
    while (index + 1 < pages.length && pages[index + 1] === end + 1) {
      end = pages[++index];
    }
    parts.push(start === end ? String(start) : `${start}-${end}`);
  }
  return parts.join(', ');
}

function OrderListItem({ order, onTrack }: { order: CloudOrder; onTrack: () => void }) {
  return (
    <article className="order-list-item">
      <div>
        <span className={`status-pill ${statusClass(order.status)}`}><Icon name={statusIcon(order.status)} />{statusLabel(order.status)}</span>
        <strong>{compactFileName(order.fileName)}</strong>
        <small>{order.shopName || order.shopId} · {visibleQueueToken(order)}</small>
        <small>{orderDateLabel(order)} · {orderPriceLabel(order)}</small>
      </div>
      <button className="secondary-button" type="button" onClick={onTrack}><Icon name="orders" /><span>View order</span></button>
    </article>
  );
}

function OrderGroup({ title, orders, action, onOrder }: {
  title: string;
  orders: CloudOrder[];
  action: string;
  onOrder: (order: CloudOrder) => void;
}) {
  if (!orders.length) return null;
  return (
    <section className="order-group">
      <h2>{title}</h2>
      {orders.map((order) => (
        <article className="order-list-item" key={`${order.shopId}-${order.orderId}`}>
          <div>
            <span className={`status-pill ${statusClass(order.status)}`}><Icon name={statusIcon(order.status)} />{statusLabel(order.status)}</span>
            <strong>{visibleQueueToken(order)} · {compactFileName(order.fileName)}</strong>
            <small>{orderDateLabel(order)} · {orderPriceLabel(order)}</small>
            {order.status === 'FAILED' && <small className="failure-reason">{order.statusMessage || 'Order did not complete.'}</small>}
          </div>
          <button className="secondary-button" type="button" onClick={() => onOrder(order)}><Icon name={action === 'Retry' || action === 'Reorder' ? 'refresh' : 'orders'} /><span>{action}</span></button>
        </article>
      ))}
    </section>
  );
}

function ThemeSwitcher({
  preference,
  resolved,
  onChange,
}: {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  onChange: (theme: ThemePreference) => void;
}) {
  const options: { value: ThemePreference; label: string }[] = [
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
    { value: 'system', label: 'Auto' },
  ];

  return (
    <div className="theme-switcher">
      <span>Appearance ({resolved === 'dark' ? 'Dark' : 'Light'}{preference === 'system' ? ', system' : ''})</span>
      <div className="theme-options" role="group" aria-label="Theme">
        {options.map((option) => (
          <button
            className={preference === option.value ? 'selected' : ''}
            type="button"
            key={option.value}
            aria-pressed={preference === option.value}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function BottomNav({ active, onChange, pendingPaymentCount }: { active: AppTab; onChange: (tab: AppTab) => void; pendingPaymentCount: number }) {
  const tabs: AppTab[] = ['home', 'docs', 'orders'];
  const icons: Record<AppTab, IconName> = { home: 'home', docs: 'folder', orders: 'orders', profile: 'user' };
  return (
    <nav className="bottom-nav" aria-label="Main navigation">
      {tabs.map((tab) => (
        <button className={active === tab ? 'selected' : ''} type="button" key={tab} onClick={() => onChange(tab)}>
          <Icon name={icons[tab]} />
          <span className="bottom-nav-label">{tab === 'orders' ? 'My Orders' : tab[0].toUpperCase() + tab.slice(1)}</span>
          {tab === 'orders' && pendingPaymentCount > 0 && <b className="bottom-nav-badge" aria-label={`${pendingPaymentCount} payment${pendingPaymentCount === 1 ? '' : 's'} pending`}>{pendingPaymentCount > 9 ? '9+' : pendingPaymentCount}</b>}
        </button>
      ))}
    </nav>
  );
}

function visibleQueueToken(order: CloudOrder) {
  if (order.queueToken) return order.queueToken;
  let hash = 0;
  for (const character of order.orderId) hash = ((hash * 31) + character.charCodeAt(0)) >>> 0;
  return `P-${String((hash % 99) + 1).padStart(2, '0')}`;
}

function statusLabel(status: string) {
  const normalized = status.toUpperCase();
  if (normalized === 'PRINTED') return 'Ready';
  if (normalized === 'PRINTING') return 'Printing';
  if (normalized === 'AGENT_DOWNLOADING') return 'Preparing';
  if (normalized === 'FAILED') return 'Failed';
  if (normalized === 'COLLECTED') return 'Print collected';
  if (normalized === 'PAID' || normalized === 'READY_TO_PRINT') return 'Queued';
  if (normalized === 'PAYMENT_EXPIRED') return 'Payment expired';
  if (normalized === 'CONVERSION_PENDING') return 'Waiting to convert';
  if (normalized === 'CONVERTING') return 'Converting to PDF';
  if (normalized === 'CONVERSION_READY') return 'PDF ready to review';
  if (normalized === 'AWAITING_PAYMENT' || normalized === 'PAYMENT_SESSION_CREATED' || normalized === 'PAYMENT_PENDING') return 'Payment pending';
  if (normalized.includes('PAYMENT')) return 'Payment';
  return 'Uploaded';
}

function statusClass(status: string) {
  const normalized = status.toUpperCase();
  if (normalized === 'PRINTED' || normalized === 'COLLECTED') return 'ready';
  if (normalized === 'FAILED') return 'failed';
  if (normalized === 'PRINTING') return 'printing';
  return 'active';
}

function statusIcon(status: string): IconName {
  const normalized = status.toUpperCase();
  if (normalized === 'PRINTED' || normalized === 'COLLECTED') return 'check';
  if (normalized === 'FAILED') return 'trash';
  if (normalized === 'PRINTING') return 'printer';
  if (normalized.includes('PAYMENT')) return 'credit-card';
  return 'clock';
}

function ImageRotationControls({ rotation, onRotate, compact = false }: { rotation: ImageRotation; onRotate: (delta: -90 | 90) => void; compact?: boolean }) {
  return (
    <div className={`image-rotation-controls${compact ? ' compact' : ''}`} role="group" aria-label="Rotate image">
      <span>Rotate image</span>
      <button type="button" onClick={() => onRotate(-90)} aria-label="Rotate image left 90 degrees">↶ <span>Left</span></button>
      <strong>{rotation}°</strong>
      <button type="button" onClick={() => onRotate(90)} aria-label="Rotate image right 90 degrees"><span>Right</span> ↷</button>
    </div>
  );
}

function WordDocumentPreview({ pages }: {
  pages: number;
  paperSize: 'A4' | 'Legal';
  orientation: 'Auto' | 'Portrait' | 'Landscape';
}) {
  return (
    <div className="word-preview" role="img" aria-label="Microsoft Word document awaiting PDF conversion">
      <span className="word-preview-icon"><Icon name="file" /></span>
      <strong>Microsoft Word document</strong>
      <span>{pages > 1 ? `${pages} saved pages (provisional)` : 'Page count pending'} · Native layout</span>
      <small>Converted securely by Microsoft Word on the shop computer, then previewed as PDF before pricing.</small>
    </div>
  );
}

function FinalImagePagePreview({
  url,
  layout,
  rotation,
  paperSize,
  orientation,
  mode,
}: {
  url: string;
  layout: ImageLayout;
  rotation: ImageRotation;
  paperSize: 'A4' | 'Legal';
  orientation: 'Auto' | 'Portrait' | 'Landscape';
  mode: 'bw' | 'color';
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [contentSize, setContentSize] = useState({ width: 0, height: 0 });
  const quarterTurn = rotation === 90 || rotation === 270;
  const rotatedWidth = contentSize.width ? (quarterTurn ? contentSize.height : contentSize.width) : 0;
  const rotatedHeight = contentSize.height ? (quarterTurn ? contentSize.width : contentSize.height) : 0;
  const resolvedOrientation = resolvePrintOrientation(orientation, rotatedWidth, rotatedHeight);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const image = new Image();

    const render = () => {
      if (!image.naturalWidth || !image.naturalHeight) return;

      const bounds = canvas.getBoundingClientRect();
      if (!bounds.width || !bounds.height) return;
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.round(bounds.width * pixelRatio));
      canvas.height = Math.max(1, Math.round(bounds.height * pixelRatio));
      const context = canvas.getContext('2d');
      if (!context) return;
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      context.clearRect(0, 0, bounds.width, bounds.height);
      context.fillStyle = '#fff';
      context.fillRect(0, 0, bounds.width, bounds.height);

      // Match local-agent DrawOnPage with Margins=0: use the full page box (hardware margins only exist on the printer).
      const printable = { x: 0, y: 0, width: bounds.width, height: bounds.height };
      const coverageScale = layout === 'Half page' ? Math.SQRT1_2 : layout === 'Quarter page' ? 0.5 : 1;
      const target = {
        x: printable.x + printable.width * (1 - coverageScale) / 2,
        y: printable.y + printable.height * (1 - coverageScale) / 2,
        width: printable.width * coverageScale,
        height: printable.height * coverageScale,
      };
      const contentW = quarterTurn ? image.naturalHeight : image.naturalWidth;
      const contentH = quarterTurn ? image.naturalWidth : image.naturalHeight;
      let scale: number;
      if (layout === 'Original size') {
        const page = paperDimensionsInches(paperSize, resolvePrintOrientation(orientation, contentW, contentH) === 'Landscape');
        scale = (bounds.width / page.width) / 96;
      } else {
        const widthScale = target.width / contentW;
        const heightScale = target.height / contentH;
        scale = layout === 'Fill page' ? Math.max(widthScale, heightScale) : Math.min(widthScale, heightScale);
      }

      context.save();
      context.beginPath();
      context.rect(printable.x, printable.y, printable.width, printable.height);
      context.clip();
      context.translate(printable.x + printable.width / 2, printable.y + printable.height / 2);
      context.rotate(rotation * Math.PI / 180);
      context.filter = mode === 'bw' ? 'grayscale(1)' : 'none';
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.drawImage(
        image,
        -image.naturalWidth * scale / 2,
        -image.naturalHeight * scale / 2,
        image.naturalWidth * scale,
        image.naturalHeight * scale,
      );
      context.restore();
    };

    image.onload = () => {
      setContentSize({ width: image.naturalWidth, height: image.naturalHeight });
      render();
    };
    image.src = url;
    const observer = new ResizeObserver(render);
    observer.observe(canvas);
    return () => {
      observer.disconnect();
      image.onload = null;
    };
  }, [layout, mode, orientation, paperSize, quarterTurn, rotation, url]);

  return (
    <>
      <div className="final-page-settings">
        <span>{paperSize}</span>
        <span>{resolvedOrientation}</span>
        <span>{mode === 'bw' ? 'B/W' : 'Colour'}</span>
        <span>{layout}</span>
        <span>{rotation}°</span>
      </div>
      <div className={`final-page-preview ${pagePreviewClass(paperSize, resolvedOrientation)}`}>
        <canvas ref={canvasRef} className="final-image-canvas" role="img" aria-label={`Image on ${paperSize} paper, rotated ${rotation} degrees`} />
      </div>
    </>
  );
}

function FinalPdfPagePreview({
  url,
  paperSize,
  orientation,
  mode,
  pageLabel,
  totalPages,
}: {
  url: string;
  paperSize: 'A4' | 'Legal';
  orientation: 'Auto' | 'Portrait' | 'Landscape';
  mode: 'bw' | 'color';
  pageLabel: string;
  totalPages: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [pageSize, setPageSize] = useState({ width: 0, height: 0 });
  const [renderState, setRenderState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [previewPage, setPreviewPage] = useState(1);
  const [previewPageCount, setPreviewPageCount] = useState(totalPages);
  const resolvedOrientation = resolvePrintOrientation(orientation, pageSize.width, pageSize.height);

  useEffect(() => {
    setPreviewPage(1);
    setPreviewPageCount(totalPages);
  }, [totalPages, url]);

  useEffect(() => {
    let disposed = false;
    let renderSequence = 0;
    let observer: ResizeObserver | undefined;
    let loadingTask: { destroy: () => Promise<void> } | undefined;

    setPageSize({ width: 0, height: 0 });
    setRenderState('loading');

    (async () => {
      try {
        const pdfjs = await import('pdfjs-dist');
        pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
        const task = pdfjs.getDocument({ url });
        loadingTask = task;
        const pdf = await task.promise;
        if (disposed) return;
        const pageCount = pdf.numPages;
        setPreviewPageCount(pageCount);
        const currentPage = Math.min(previewPage, pageCount);
        if (currentPage !== previewPage) setPreviewPage(currentPage);
        const page = await pdf.getPage(currentPage);
        const sourceViewport = page.getViewport({ scale: 1 });
        if (disposed) return;
        setPageSize({ width: sourceViewport.width, height: sourceViewport.height });

        const render = async () => {
          const canvas = canvasRef.current;
          if (!canvas || disposed) return;
          const bounds = canvas.getBoundingClientRect();
          if (!bounds.width || !bounds.height) return;

          const sequence = ++renderSequence;
          const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
          const fitScale = Math.min(bounds.width / sourceViewport.width, bounds.height / sourceViewport.height);
          const viewport = page.getViewport({ scale: fitScale * pixelRatio });
          const renderedPage = window.document.createElement('canvas');
          renderedPage.width = Math.max(1, Math.round(viewport.width));
          renderedPage.height = Math.max(1, Math.round(viewport.height));
          await page.render({ canvas: renderedPage, viewport, background: '#fff' }).promise;
          if (disposed || sequence !== renderSequence) return;

          canvas.width = Math.max(1, Math.round(bounds.width * pixelRatio));
          canvas.height = Math.max(1, Math.round(bounds.height * pixelRatio));
          const context = canvas.getContext('2d');
          if (!context) throw new Error('Canvas rendering is unavailable.');
          context.fillStyle = '#fff';
          context.fillRect(0, 0, canvas.width, canvas.height);
          context.filter = mode === 'bw' ? 'grayscale(1)' : 'none';
          context.imageSmoothingEnabled = true;
          context.imageSmoothingQuality = 'high';
          const sourceBounds = resolvePdfPreviewContentBounds(
            renderedPage,
            orientation,
            sourceViewport.width,
            sourceViewport.height,
          );
          const contentScale = Math.min(canvas.width / sourceBounds.width, canvas.height / sourceBounds.height);
          const contentWidth = Math.max(1, Math.round(sourceBounds.width * contentScale));
          const contentHeight = Math.max(1, Math.round(sourceBounds.height * contentScale));
          context.drawImage(
            renderedPage,
            sourceBounds.x,
            sourceBounds.y,
            sourceBounds.width,
            sourceBounds.height,
            Math.round((canvas.width - contentWidth) / 2),
            Math.round((canvas.height - contentHeight) / 2),
            contentWidth,
            contentHeight,
          );
          context.filter = 'none';
          setRenderState('ready');
        };

        observer = new ResizeObserver(() => void render());
        const canvas = canvasRef.current;
        if (canvas) observer.observe(canvas);
        await render();
      } catch {
        if (!disposed) setRenderState('error');
      }
    })();

    return () => {
      disposed = true;
      renderSequence += 1;
      observer?.disconnect();
      void loadingTask?.destroy();
    };
  }, [mode, orientation, previewPage, totalPages, url]);

  return (
    <>
      <div className="final-page-settings">
        <span>{paperSize}</span>
        <span>{resolvedOrientation}</span>
        <span>{mode === 'bw' ? 'B/W' : 'Colour'}</span>
        <span>{pageLabel}</span>
        <span>{previewPageCount} page{previewPageCount === 1 ? '' : 's'}</span>
      </div>
      {previewPageCount > 1 && <div className="pdf-page-navigation" aria-label="PDF page navigation">
        <button type="button" onClick={() => setPreviewPage((current) => Math.max(1, current - 1))} disabled={previewPage === 1}>Previous page</button>
        <span aria-live="polite">Page {previewPage} of {previewPageCount}</span>
        <button type="button" onClick={() => setPreviewPage((current) => Math.min(previewPageCount, current + 1))} disabled={previewPage === previewPageCount}>Next page</button>
      </div>}
      {previewPageCount > 1 && <PdfPageThumbnails url={url} pageCount={previewPageCount} activePage={previewPage} onSelect={setPreviewPage} />}
      <div className={`final-page-preview ${pagePreviewClass(paperSize, resolvedOrientation)}`}>
        <canvas ref={canvasRef} className="final-pdf-canvas" role="img" aria-label={`PDF page ${previewPage} of ${previewPageCount} fitted on ${paperSize} paper`} />
        {renderState === 'loading' && <span className="final-pdf-status">Rendering page…</span>}
        {renderState === 'error' && <strong className="final-pdf-status error">PDF preview is unavailable.</strong>}
      </div>
    </>
  );
}

function PdfPageThumbnails({
  url,
  pageCount,
  activePage,
  onSelect,
}: {
  url: string;
  pageCount: number;
  activePage: number;
  onSelect: (page: number) => void;
}) {
  const thumbnailRefs = useRef(new Map<number, HTMLCanvasElement>());

  useEffect(() => {
    const activeThumbnail = thumbnailRefs.current.get(activePage)?.closest('button');
    activeThumbnail?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [activePage]);

  useEffect(() => {
    let disposed = false;
    let loadingTask: { destroy: () => Promise<void> } | undefined;

    (async () => {
      try {
        const pdfjs = await import('pdfjs-dist');
        pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
        const task = pdfjs.getDocument({ url });
        loadingTask = task;
        const pdf = await task.promise;
        for (let pageNumber = 1; pageNumber <= Math.min(pdf.numPages, pageCount); pageNumber += 1) {
          if (disposed) return;
          const canvas = thumbnailRefs.current.get(pageNumber);
          if (!canvas) continue;
          const page = await pdf.getPage(pageNumber);
          const sourceViewport = page.getViewport({ scale: 1 });
          const scale = Math.min(0.18, 72 / Math.max(sourceViewport.width, sourceViewport.height));
          const viewport = page.getViewport({ scale: Math.max(scale, 0.05) });
          canvas.width = Math.max(1, Math.round(viewport.width));
          canvas.height = Math.max(1, Math.round(viewport.height));
          const context = canvas.getContext('2d');
          if (!context) continue;
          await page.render({ canvas, viewport, background: '#fff' }).promise;
        }
      } catch {
        // The main preview reports PDF failures. Thumbnails are an enhancement.
      }
    })();

    return () => {
      disposed = true;
      void loadingTask?.destroy();
    };
  }, [pageCount, url]);

  return (
    <div className="pdf-page-thumbnails" aria-label="PDF page thumbnails">
      {Array.from({ length: pageCount }, (_, index) => {
        const pageNumber = index + 1;
        return (
          <button
            className={pageNumber === activePage ? 'selected' : ''}
            type="button"
            key={pageNumber}
            aria-label={`View page ${pageNumber}`}
            aria-pressed={pageNumber === activePage}
            onClick={() => onSelect(pageNumber)}
          >
            <canvas ref={(canvas) => {
              if (canvas) thumbnailRefs.current.set(pageNumber, canvas);
              else thumbnailRefs.current.delete(pageNumber);
            }} />
            <span>{pageNumber}</span>
          </button>
        );
      })}
    </div>
  );
}

function Modal({ title, className = '', onClose, children }: { title: string; className?: string; onClose: () => void; children: React.ReactNode }) {
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const shell = window.document.querySelector<HTMLElement>('.shell');
    const previousOverflow = shell?.style.overflowY || '';
    if (shell) shell.style.overflowY = 'hidden';
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      if (shell) shell.style.overflowY = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className={`modal ${className}`} role="dialog" aria-modal="true" aria-label={title} onClick={(event) => event.stopPropagation()}>
        <div className="modal-head">
          <h2>{title}</h2>
          <button className="modal-close" type="button" aria-label="Close preview" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

function PrintStatusTimeline({ order, compact = false }: { order: CloudOrder; compact?: boolean }) {
  const normalizedStatus = order.status.toUpperCase();
  if (normalizedStatus === 'FAILED') {
    return (
      <div className="status-timeline">
        <div className="status-step active">
          <span>!</span>
          <div>
            <strong>{order.cancelledBy === 'CUSTOMER' ? 'Cancelled' : 'Failed'}</strong>
            <small>{order.statusMessage || 'This print job was removed from the queue'}</small>
          </div>
        </div>
      </div>
    );
  }

  const paymentPending = ['AWAITING_PAYMENT', 'PAYMENT_SESSION_CREATED', 'PAYMENT_PENDING', 'PAYMENT_EXPIRED'].includes(normalizedStatus);
  const manualPayment = order.paymentMode === 'MANUAL' || order.paymentMode === 'MANUAL_CASHIER';
  const pinPayment = order.paymentMode === 'DEMO_PIN' || order.paymentMode === 'COUNTER_PIN';
  const demoPin = order.paymentMode === 'DEMO_PIN';
  const stages = [
    { title: 'Document uploaded', detail: 'Your document and print settings are saved' },
    { title: paymentPending ? (pinPayment ? (demoPin ? 'Enter Demo PIN' : 'Enter Counter PIN') : manualPayment ? 'Pay at shop' : 'Complete payment') : (pinPayment ? (demoPin ? 'Demo approved' : 'Counter payment approved') : manualPayment ? 'Manual payment confirmed' : 'Payment confirmed'), detail: paymentPending ? (pinPayment ? (demoPin ? 'No payment is collected in demo mode' : 'Pay the shopkeeper first, then enter the shop PIN') : manualPayment ? 'Pay by cash or direct UPI; the shopkeeper confirms your token in the agent' : 'Complete secure payment to join the print queue') : 'Your order is in the shop queue' },
    { title: 'Ready to print', detail: 'The shop has received your document' },
    { title: 'Printing', detail: 'The shop is printing your document' },
    { title: 'Ready for collection', detail: 'Collect your print from the shop' },
    { title: 'Collected', detail: 'Order completed' },
  ];
  const progressByStatus: Record<string, number> = {
    UPLOAD_PENDING: 0,
    CONVERSION_PENDING: 0,
    CONVERTING: 0,
    CONVERSION_READY: 0,
    AWAITING_PAYMENT: 1,
    PAYMENT_SESSION_CREATED: 1,
    PAYMENT_PENDING: 1,
    PAYMENT_EXPIRED: 1,
    PAID: 2,
    AGENT_DOWNLOADING: 2,
    READY_TO_PRINT: 2,
    PRINTING: 3,
    PRINTED: 4,
    COLLECTED: 5,
  };
  const activeIndex = progressByStatus[normalizedStatus] ?? 0;

  if (compact) {
    const activeStage = stages[activeIndex];
    return (
      <details className="status-timeline status-timeline-compact">
        <summary>
          <span className="status-step active"><span>{activeIndex + 1}</span><div><strong>{activeStage.title}</strong><small>{activeStage.detail}</small></div></span>
          <small>View progress</small>
        </summary>
        <div className="status-progress-dots" aria-label={`Step ${activeIndex + 1} of ${stages.length}`}>
          {stages.map((stage, index) => <span className={index <= activeIndex ? 'complete' : ''} key={stage.title} />)}
        </div>
      </details>
    );
  }

  return (
    <div className="status-timeline">
      {stages.map((stage, index) => {
        const state = index < activeIndex || normalizedStatus === 'COLLECTED' ? 'done' : index === activeIndex ? 'active' : 'pending';
        return (
          <div className={`status-step ${state}`} key={stage.title}>
            <span>{state === 'done' ? <Icon name="check" /> : index + 1}</span>
            <div>
              <strong>{stage.title}</strong>
              <small>{stage.detail}</small>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PaymentConfirmationWait({ seconds, compact = false }: { seconds: number; compact?: boolean }) {
  return (
    <div className={`payment-confirmation-wait${compact ? ' compact' : ''}`} role="status" aria-live="polite">
      <span className="payment-confirmation-spinner" aria-hidden="true"><Icon name="clock" /></span>
      <div>
        <strong>Confirming your payment</strong>
        <span>Do not pay again. We will check automatically{seconds > 0 ? ` in ${seconds}s` : ' now'}.</span>
      </div>
    </div>
  );
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(value || 0));
}

function orderPriceLabel(order: CloudOrder) {
  return Number(order.amount || 0) > 0 ? formatCurrency(order.amount) : 'Price pending';
}

function compactFileName(value: string, maximumLength = 30) {
  const name = String(value || 'Document');
  if (name.length <= maximumLength) return name;
  const dot = name.lastIndexOf('.');
  const extension = dot > 0 && name.length - dot <= 8 ? name.slice(dot) : '';
  const available = Math.max(8, maximumLength - extension.length - 1);
  return `${name.slice(0, available)}…${extension}`;
}

function orderDateLabel(order: CloudOrder) {
  const date = new Date(order.createdAt || order.updatedAt || '');
  if (Number.isNaN(date.getTime())) return 'Recent order';
  return new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }).format(date);
}

function trackingExceptionMessage(order: CloudOrder) {
  const status = order.status.toUpperCase();
  if (status === 'PAYMENT_EXPIRED') return 'The payment link expired. Start again to create a new order.';
  if (order.paymentMode === 'DEMO_PIN' && status === 'AWAITING_PAYMENT') return 'Ask the shopkeeper for the running Demo PIN. No payment will be collected.';
  if (order.paymentMode === 'COUNTER_PIN' && status === 'AWAITING_PAYMENT') return 'Pay the shopkeeper by cash or direct UPI, then enter the running Counter PIN.';
  if (order.paymentMode === 'MANUAL' && status === 'AWAITING_PAYMENT') return `Pay manually at the shop and give token ${visibleQueueToken(order)} to the shopkeeper.`;
  if (status === 'PAYMENT_PENDING') return 'Payment confirmation is in progress. If you have already completed payment, do not pay again.';
  if (status === 'PAYMENT_SESSION_CREATED' || status === 'AWAITING_PAYMENT') return 'Complete payment using the secure payment link to continue.';
  return '';
}

function paymentNextStepMessage(paymentMode: string | undefined, prefix: string) {
  if (paymentMode === 'DEMO_PIN') return `${prefix} Enter the shop's Demo PIN. No payment will be collected.`;
  if (paymentMode === 'COUNTER_PIN') return `${prefix} Pay the shopkeeper by cash or direct UPI, then enter the Counter PIN.`;
  if (paymentMode === 'MANUAL') return `${prefix} Pay manually at the shop and give your token to the shopkeeper.`;
  return `${prefix} Preparing Razorpay payment…`;
}

function gatewayDisplayName(provider: string | undefined) {
  const value = String(provider || '').trim().toLowerCase();
  if (value === 'razorpay') return 'Razorpay';
  if (value === 'cashfree') return 'Cashfree';
  if (value === 'paytm') return 'Paytm';
  if (value === 'payu') return 'PayU';
  if (value === 'phonepe') return 'PhonePe';
  return 'the payment provider';
}

function isShopOpenNow(shop: Pick<PublicShop, 'openingTime' | 'closingTime' | 'printingPaused'> | null | undefined, now = Date.now()) {
  if (shop?.printingPaused === true) return false;
  const openingTime = String(shop?.openingTime || '').trim();
  const closingTime = String(shop?.closingTime || '').trim();
  if (!openingTime && !closingTime) return true;
  if (!/^\d{2}:\d{2}$/.test(openingTime) || !/^\d{2}:\d{2}$/.test(closingTime)) return false;
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(now);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  const currentTime = `${values.hour}:${values.minute}`;
  return currentTime >= openingTime && currentTime < closingTime;
}

function shopClosedMessage(shop: Pick<PublicShop, 'openingTime' | 'closingTime' | 'printingPaused'> | null | undefined) {
  if (shop?.printingPaused === true) return 'This shop has temporarily paused printing. Please choose another shop or try again when it resumes.';
  return shop?.openingTime && shop?.closingTime
    ? `This shop is closed. Printing is available from ${shop.openingTime} to ${shop.closingTime}.`
    : 'This shop is closed right now.';
}

function normalizeImageRotation(value: unknown): ImageRotation {
  const rotation = Number(value);
  return rotation === 90 || rotation === 180 || rotation === 270 ? rotation : 0;
}

function normalizeImageLayout(value: unknown): ImageLayout {
  switch (value) {
    case 'Half page': return 'Half page';
    case 'Quarter page': return 'Quarter page';
    case 'Fill':
    case 'Fill page': return 'Fill page';
    case 'Actual size':
    case 'Original size': return 'Original size';
    default: return 'Full page';
  }
}

function imageLayoutClass(layout: ImageLayout) {
  if (layout === 'Fill page') return 'fill';
  if (layout === 'Original size') return 'actual';
  if (layout === 'Half page') return 'half';
  if (layout === 'Quarter page') return 'quarter';
  return 'fit';
}

function imageLayoutHelp(layout: ImageLayout) {
  if (layout === 'Half page') return 'Half page keeps the whole image centred within half of the selected paper area.';
  if (layout === 'Quarter page') return 'Quarter page keeps the whole image centred within one quarter of the selected paper area.';
  if (layout === 'Fill page') return 'Fill page covers the paper and may crop image edges.';
  if (layout === 'Original size') return 'Original size uses the image’s embedded DPI and may crop if it is larger than the paper.';
  return 'Full page keeps the whole image visible and may leave empty strips when its shape differs from the paper.';
}

function resolvePdfPreviewContentBounds(
  page: HTMLCanvasElement,
  orientation: 'Auto' | 'Portrait' | 'Landscape',
  sourcePageWidth: number,
  sourcePageHeight: number,
) {
  const fullPage = { x: 0, y: 0, width: page.width, height: page.height };
  if (orientation === 'Auto') return fullPage;

  const requestedLandscape = orientation === 'Landscape';
  const nativeLandscape = sourcePageWidth > sourcePageHeight;
  const nativeOrientationMatches = nativeLandscape === requestedLandscape;

  const context = page.getContext('2d', { willReadFrequently: true });
  if (!context) return fullPage;
  const pixels = context.getImageData(0, 0, page.width, page.height).data;
  const step = Math.max(1, Math.floor(Math.min(page.width, page.height) / 700));
  let left = page.width;
  let top = page.height;
  let right = -1;
  let bottom = -1;

  for (let y = 0; y < page.height; y += step) {
    for (let x = 0; x < page.width; x += step) {
      const offset = ((y * page.width) + x) * 4;
      if (pixels[offset + 3] < 16 ||
          (pixels[offset] >= 248 && pixels[offset + 1] >= 248 && pixels[offset + 2] >= 248)) continue;
      left = Math.min(left, x);
      top = Math.min(top, y);
      right = Math.max(right, x);
      bottom = Math.max(bottom, y);
    }
  }

  if (right < left || bottom < top) return fullPage;
  const contentWidth = right - left + step;
  const contentHeight = bottom - top + step;
  if ((contentWidth > contentHeight) !== requestedLandscape) return fullPage;
  if (nativeOrientationMatches && (contentWidth >= page.width * 0.75 || contentHeight >= page.height * 0.75)) return fullPage;
  if (!nativeOrientationMatches && contentWidth >= page.width * 0.9 && contentHeight >= page.height * 0.9) return fullPage;

  const padding = Math.max(2, Math.round(Math.min(page.width, page.height) * 0.015));
  left = Math.max(0, left - padding);
  top = Math.max(0, top - padding);
  right = Math.min(page.width - 1, right + step + padding);
  bottom = Math.min(page.height - 1, bottom + step + padding);
  return { x: left, y: top, width: right - left + 1, height: bottom - top + 1 };
}

function resolvePrintOrientation(
  orientation: 'Auto' | 'Portrait' | 'Landscape',
  contentWidth = 0,
  contentHeight = 0,
): 'Portrait' | 'Landscape' {
  if (orientation === 'Landscape') return 'Landscape';
  if (orientation === 'Portrait') return 'Portrait';
  // Auto: same rule as local-agent DocumentPrintService.ShouldUseLandscape
  return contentWidth > contentHeight ? 'Landscape' : 'Portrait';
}

function paperDimensionsInches(paperSize: 'A4' | 'Legal', landscape: boolean) {
  if (paperSize === 'Legal') {
    return landscape ? { width: 14, height: 8.5 } : { width: 8.5, height: 14 };
  }
  return landscape ? { width: 297 / 25.4, height: 210 / 25.4 } : { width: 210 / 25.4, height: 297 / 25.4 };
}

function pagePreviewClass(paperSize: 'A4' | 'Legal', orientation: 'Portrait' | 'Landscape') {
  return `${paperSize === 'Legal' ? 'legal' : 'a4'} ${orientation.toLowerCase()}`;
}

function canCustomerCancel(status: string) {
  return ['UPLOAD_PENDING', 'CONVERSION_PENDING', 'CONVERTING', 'CONVERSION_READY', 'AWAITING_PAYMENT', 'PAYMENT_SESSION_CREATED', 'PAYMENT_PENDING', 'PAYMENT_EXPIRED', 'PAID']
    .includes(status.toUpperCase());
}

function readBase64(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || '').split(',')[1] || '');
    reader.onerror = () => reject(new Error('Could not read selected document.'));
    reader.readAsDataURL(file);
  });
}

function cleanShop(value: string) {
  const normalized = value.trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '-').slice(0, 40);
  // Early SaralPrint shops used a ten-character hexadecimal id stored with
  // an SP- prefix. Accept the shorter value shopkeepers commonly share.
  return /^[A-F0-9]{10}$/.test(normalized) ? `SP-${normalized}` : normalized;
}

function readShopCode(rawValue: string) {
  const raw = rawValue.trim();
  if (!raw) return '';

  try {
    const url = new URL(raw);
    return url.searchParams.get('shop') || url.searchParams.get('code') || url.pathname.split('/').filter(Boolean).pop() || '';
  } catch {
    return raw;
  }
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

async function optimizeImageForUpload(
  file: File,
  paperSize: 'A4' | 'Legal',
  layout: ImageLayout,
  onStatus: (status: string) => void,
) {
  const requiresPrintSafeFormat = file.type === 'image/webp';
  const shouldResize = file.size > imageOptimizationThresholdBytes && layout !== 'Original size';
  if (!file.type.startsWith('image/') || (!requiresPrintSafeFormat && !shouldResize)) {
    return file;
  }

  onStatus(requiresPrintSafeFormat ? 'Preparing image for printing...' : 'Optimizing image for faster upload...');
  try {
    const image = await loadImage(file);
    const maximumPixels = paperSize === 'Legal'
      ? { width: 2550, height: 4200 }
      : { width: 2480, height: 3508 };
    const portrait = image.naturalHeight >= image.naturalWidth;
    const maximumWidth = portrait ? maximumPixels.width : maximumPixels.height;
    const maximumHeight = portrait ? maximumPixels.height : maximumPixels.width;
    const scale = shouldResize
      ? Math.min(1, maximumWidth / image.naturalWidth, maximumHeight / image.naturalHeight)
      : 1;
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));
    const canvas = window.document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const outputType = requiresPrintSafeFormat
      ? 'image/jpeg'
      : ['image/jpeg', 'image/png'].includes(file.type) ? file.type : 'image/jpeg';
    const context = canvas.getContext('2d', { alpha: outputType === 'image/png' });
    if (!context) return file;
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    if (outputType !== 'image/png') {
      context.fillStyle = '#fff';
      context.fillRect(0, 0, width, height);
    }
    context.drawImage(image, 0, 0, width, height);

    let blob: Blob;
    if (outputType === 'image/png') {
      blob = await canvasToBlob(canvas, outputType);
    } else {
      blob = await bestQualityBlob(canvas, outputType, imageOptimizationTargetBytes);
    }

    if (!requiresPrintSafeFormat && blob.size >= file.size) return file;
    const outputName = requiresPrintSafeFormat
      ? file.name.replace(/(?:\.[^.]+)?$/, '.jpg')
      : file.name;
    return new File([blob], outputName, { type: blob.type || outputType, lastModified: file.lastModified });
  } catch {
    if (requiresPrintSafeFormat) {
      throw new Error('This WebP image could not be prepared for printing. Download it as JPG or PNG and try again.');
    }
    return file;
  }
}

async function convertImageToPrintPdf(
  file: File,
  paperSize: 'A4' | 'Legal',
  orientation: 'Auto' | 'Portrait' | 'Landscape',
  layout: ImageLayout,
  rotation: ImageRotation,
) {
  const imageFile = await normalizeImageForPdf(file);
  const image = await loadImage(imageFile);
  const quarterTurn = rotation === 90 || rotation === 270;
  const rotatedWidth = quarterTurn ? image.naturalHeight : image.naturalWidth;
  const rotatedHeight = quarterTurn ? image.naturalWidth : image.naturalHeight;
  const resolvedOrientation = resolvePrintOrientation(orientation, rotatedWidth, rotatedHeight);
  const paper = paperDimensionsInches(paperSize, resolvedOrientation === 'Landscape');
  const pageWidth = paper.width * 72;
  const pageHeight = paper.height * 72;
  const coverageScale = layout === 'Half page' ? Math.SQRT1_2 : layout === 'Quarter page' ? 0.5 : 1;
  const targetWidth = pageWidth * coverageScale;
  const targetHeight = pageHeight * coverageScale;
  const scale = layout === 'Original size'
    ? 72 / 96
    : layout === 'Fill page'
      ? Math.max(targetWidth / rotatedWidth, targetHeight / rotatedHeight)
      : Math.min(targetWidth / rotatedWidth, targetHeight / rotatedHeight);
  const width = image.naturalWidth * scale;
  const height = image.naturalHeight * scale;
  const { PDFDocument, degrees } = await import('pdf-lib');
  const pdf = await PDFDocument.create();
  pdf.setTitle(file.name);
  pdf.setCreator('SaralPrint');
  const embedded = imageFile.type === 'image/png'
    ? await pdf.embedPng(await imageFile.arrayBuffer())
    : await pdf.embedJpg(await imageFile.arrayBuffer());
  const page = pdf.addPage([pageWidth, pageHeight]);
  const placement = rotatedImagePlacement(pageWidth, pageHeight, width, height, rotation);
  page.drawImage(embedded, { ...placement, width, height, rotate: degrees(rotation) });
  const bytes = await pdf.save();
  const name = file.name.replace(/(?:\.[^.]+)?$/, '.pdf');
  return new File([Uint8Array.from(bytes).buffer], name, { type: 'application/pdf', lastModified: file.lastModified });
}

function rotatedImagePlacement(pageWidth: number, pageHeight: number, width: number, height: number, rotation: ImageRotation) {
  if (rotation === 90) return { x: (pageWidth + height) / 2, y: (pageHeight - width) / 2 };
  if (rotation === 180) return { x: (pageWidth + width) / 2, y: (pageHeight + height) / 2 };
  if (rotation === 270) return { x: (pageWidth - height) / 2, y: (pageHeight + width) / 2 };
  return { x: (pageWidth - width) / 2, y: (pageHeight - height) / 2 };
}

function loadImage(file: File) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not optimize this image.'));
    };
    image.src = url;
  });
}

async function bestQualityBlob(canvas: HTMLCanvasElement, type: string, targetBytes: number) {
  let best = await canvasToBlob(canvas, type, 0.88);
  if (best.size <= targetBytes) return best;

  let low = 0.7;
  let high = 0.88;
  for (let attempt = 0; attempt < 5; attempt += 1) {
    const quality = (low + high) / 2;
    const candidate = await canvasToBlob(canvas, type, quality);
    if (candidate.size <= targetBytes) {
      best = candidate;
      low = quality;
    } else {
      high = quality;
      if (candidate.size < best.size) best = candidate;
    }
  }
  return best;
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Image optimization failed.')), type, quality);
  });
}

function formatDocumentDate(createdAt: number) {
  if (!Number.isFinite(createdAt)) return 'Saved';
  return new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short' }).format(new Date(createdAt));
}

function createBatchDocumentId() {
  return globalThis.crypto?.randomUUID?.() || `document-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

async function mergeDocumentsAsPdf(items: BatchDocumentItem[], startEachDocumentOnFront: boolean) {
  const { PDFDocument } = await import('pdf-lib');
  const mergedPdf = await PDFDocument.create();
  mergedPdf.setTitle('Combined documents');
  mergedPdf.setCreator('SaralPrint');

  for (let index = 0; index < items.length; index += 1) {
    const file = items[index].file;
    if (file.type === 'application/pdf') {
      const sourcePdf = await PDFDocument.load(await file.arrayBuffer(), {
        ignoreEncryption: false,
        updateMetadata: false,
      });
      const copiedPages = await mergedPdf.copyPages(sourcePdf, sourcePdf.getPageIndices());
      copiedPages.forEach((page) => mergedPdf.addPage(page));
    } else if (file.type.startsWith('image/')) {
      const imageFile = await normalizeImageForPdf(file);
      const imageBytes = await imageFile.arrayBuffer();
      const embeddedImage = imageFile.type === 'image/png'
        ? await mergedPdf.embedPng(imageBytes)
        : await mergedPdf.embedJpg(imageBytes);
      const portrait = embeddedImage.height >= embeddedImage.width;
      const pageWidth = portrait ? 595.28 : 841.89;
      const pageHeight = portrait ? 841.89 : 595.28;
      const margin = 24;
      const scale = Math.min(
        (pageWidth - (margin * 2)) / embeddedImage.width,
        (pageHeight - (margin * 2)) / embeddedImage.height,
      );
      const width = embeddedImage.width * scale;
      const height = embeddedImage.height * scale;
      const page = mergedPdf.addPage([pageWidth, pageHeight]);
      page.drawImage(embeddedImage, {
        x: (pageWidth - width) / 2,
        y: (pageHeight - height) / 2,
        width,
        height,
      });
    } else {
      throw new Error(`${file.name} cannot be added to a combined PDF.`);
    }

    const hasAnotherDocument = index < items.length - 1;
    if (startEachDocumentOnFront && hasAnotherDocument && mergedPdf.getPageCount() % 2 === 1) {
      const previousPage = mergedPdf.getPage(mergedPdf.getPageCount() - 1);
      const { width, height } = previousPage.getSize();
      mergedPdf.addPage([width, height]);
    }
  }

  const bytes = await mergedPdf.save();
  const date = new Date().toISOString().slice(0, 10);
  const file = new File([Uint8Array.from(bytes).buffer], `Combined documents - ${date}.pdf`, {
    type: 'application/pdf',
    lastModified: Date.now(),
  });
  return { file, pages: mergedPdf.getPageCount() };
}

async function normalizeImageForPdf(file: File) {
  if (file.type === 'image/jpeg' || file.type === 'image/png') return file;
  const image = await loadImage(file);
  const maximumDimension = 3508;
  const scale = Math.min(1, maximumDimension / Math.max(image.naturalWidth, image.naturalHeight));
  const canvas = window.document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
  const context = canvas.getContext('2d');
  if (!context) throw new Error(`Could not prepare ${file.name}.`);
  context.fillStyle = '#fff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  const blob = await canvasToBlob(canvas, 'image/jpeg', 0.9);
  return new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg', lastModified: file.lastModified });
}

async function detectDocumentPages(file: File) {
  if (file.type === 'application/pdf') {
    const { PDFDocument } = await import('pdf-lib');
    const pdf = await PDFDocument.load(await file.arrayBuffer(), { ignoreEncryption: false, updateMetadata: false });
    const pageCount = pdf.getPageCount();
    if (!pageCount || pageCount > 500) throw new Error('PDF page count is outside the supported range.');
    return pageCount;
  }

  if (isWordDocument(file)) {
    const { default: JSZip } = await import('jszip');
    let archive: Awaited<ReturnType<typeof JSZip.loadAsync>>;
    try {
      archive = await JSZip.loadAsync(await file.arrayBuffer(), { checkCRC32: false, createFolders: false });
    } catch {
      throw new Error('The selected DOCX file is damaged or invalid.');
    }
    const entries = Object.values(archive.files);
    if (entries.length > 2000) throw new Error('The selected DOCX contains too many files.');
    const unsafeEntry = entries.find((entry) => entry.name.startsWith('/') || entry.name.split('/').includes('..'));
    if (unsafeEntry) throw new Error('The selected DOCX contains an unsafe path.');
    const expandedBytes = entries.reduce((total, entry) => {
      const details = entry as typeof entry & { _data?: { uncompressedSize?: number } };
      return total + Number(details._data?.uncompressedSize || 0);
    }, 0);
    if (expandedBytes > 60 * 1024 * 1024) throw new Error('The expanded DOCX document is too large.');
    const entriesByLowerName = new Map(entries.map((entry) => [entry.name.toLowerCase(), entry]));
    if (!entriesByLowerName.has('[content_types].xml') || !entriesByLowerName.has('word/document.xml')) {
      throw new Error('The selected file is not a valid Microsoft Word DOCX document.');
    }
    if (entriesByLowerName.has('word/vbaproject.bin')) {
      throw new Error('Macro-enabled Word documents are not supported. Save it as DOCX without macros.');
    }
    const contentTypes = await entriesByLowerName.get('[content_types].xml')?.async('text') || '';
    if (!contentTypes.includes('application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml') || /macroEnabled/i.test(contentTypes)) {
      throw new Error('Only macro-free Microsoft Word DOCX documents are supported.');
    }
    const appXml = await entriesByLowerName.get('docprops/app.xml')?.async('text') || '';
    const pageCount = Number(/<Pages>(\d+)<\/Pages>/i.exec(appXml)?.[1] || 0);
    return Number.isInteger(pageCount) && pageCount >= 1 && pageCount <= 500 ? pageCount : 1;
  }

  return 1;
}

function isSupportedDocument(file: File) {
  return ['application/pdf', 'image/jpeg', 'image/png', 'image/webp', wordDocxContentType].includes(file.type) || isWordDocument(file);
}

function isWordDocument(file: Pick<File, 'name' | 'type'>) {
  return file.type === wordDocxContentType || file.name.toLowerCase().endsWith('.docx');
}

function normalizeDocumentFile(file: File) {
  if (!isWordDocument(file) || file.type === wordDocxContentType) return file;
  return new File([file], file.name, { type: wordDocxContentType, lastModified: file.lastModified });
}

function cleanFolderName(value: string) {
  return value.trim().replace(/\s+/g, ' ').slice(0, 32);
}

function cleanDocumentName(value: string, currentName: string) {
  const requested = value.trim().replace(/[\\/:*?"<>|]/g, ' ').replace(/\s+/g, ' ').slice(0, 100);
  if (!requested) return '';
  const extension = currentName.match(/\.[^.]+$/)?.[0] || '';
  return extension && !requested.toLowerCase().endsWith(extension.toLowerCase()) ? `${requested}${extension}` : requested;
}

function loadDocumentFolders() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(documentFoldersStorageKey) || '[]');
    if (!Array.isArray(parsed)) return [];
    return Array.from(new Set(parsed.map((value) => cleanFolderName(String(value))).filter(Boolean))) as string[];
  } catch {
    return [];
  }
}

function saveDocumentFolders(folders: string[]) {
  try {
    window.localStorage.setItem(documentFoldersStorageKey, JSON.stringify(folders));
  } catch {
    // Folder names are also recoverable from their documents when storage is restricted.
  }
}

function waitForVideo(ref: React.RefObject<HTMLVideoElement | null>) {
  return new Promise<HTMLVideoElement>((resolve, reject) => {
    let attempts = 0;
    const check = () => {
      if (ref.current) {
        resolve(ref.current);
        return;
      }

      attempts += 1;
      if (attempts > 20) {
        reject(new Error('Camera view was not ready.'));
        return;
      }

      window.requestAnimationFrame(check);
    };

    check();
  });
}

function cameraErrorMessage(error: unknown) {
  if (error instanceof DOMException) {
    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      return 'Camera permission was blocked. Allow camera permission in browser settings or enter the shop code manually.';
    }

    if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
      return 'No camera was found on this device. Enter the shop code manually.';
    }

    if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
      return 'Camera is already in use by another app. Close it and try again.';
    }
  }

  return error instanceof Error ? error.message : 'Could not start camera. Enter the shop code manually.';
}

function safeCheckoutUrl(value: string | undefined) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  try {
    const url = new URL(raw);
    const razorpayHost = url.hostname === 'rzp.io' || url.hostname === 'razorpay.com' || url.hostname.endsWith('.razorpay.com');
    const cashfreeHost = url.hostname === 'payments.cashfree.com' || url.hostname === 'payments-test.cashfree.com';
    const paytmHost = url.hostname === 'paytm.me' ||
      url.hostname === 'paytmpayments.com' || url.hostname.endsWith('.paytmpayments.com') ||
      url.hostname === 'paytm.in' || url.hostname.endsWith('.paytm.in');
    const payuHost = url.hostname === 'pmny.in' || url.hostname.endsWith('.pmny.in') ||
      url.hostname === 'payu.in' || url.hostname.endsWith('.payu.in');
    const phonepeHost = url.hostname === 'phonepe.com' || url.hostname.endsWith('.phonepe.com');
    return url.protocol === 'https:' && (razorpayHost || cashfreeHost || paytmHost || payuHost || phonepeHost) ? url.toString() : '';
  } catch {
    return '';
  }
}

function shouldShowStartupSplash() {
  const params = new URLSearchParams(window.location.search);
  // Gateway return URLs always carry the SaralPrint order reference. Do not
  // put an already-paying customer behind the full launch animation.
  if (params.get('order') || params.get('orderId')) return false;
  try {
    return window.sessionStorage.getItem(startupSplashStorageKey) !== 'true';
  } catch {
    // Private browsing can deny session storage. In that case, retain the
    // normal-launch animation but still skip gateway returns above.
    return true;
  }
}

async function loadShop(shopId: string) {
  const response = await fetch(`${apiBase}/shops/lookup/${encodeURIComponent(shopId)}`, {
    cache: 'no-store',
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body.error || 'Shop id is not registered.');
  }

  return body as PublicShop;
}

function openDocumentDb() {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open('saralprint-documents', 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains('documents')) {
        const store = db.createObjectStore('documents', { keyPath: 'id' });
        store.createIndex('createdAt', 'createdAt');
        store.createIndex('folder', 'folder');
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Could not open document storage.'));
  });
}

async function saveManagedDocument(file: File, folder: string) {
  const db = await openDocumentDb();
  const record: ManagedDocument = {
    id: `${Date.now()}-${crypto.randomUUID?.() || Math.random().toString(36).slice(2)}`,
    name: file.name,
    type: file.type || 'application/octet-stream',
    size: file.size,
    folder: cleanFolderName(folder) || defaultDocumentFolder,
    createdAt: Date.now(),
    blob: file,
  };

  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction('documents', 'readwrite');
    tx.objectStore('documents').put(record);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error('Could not save document.'));
    };
  });
}

async function loadManagedDocuments() {
  const db = await openDocumentDb();
  return new Promise<ManagedDocument[]>((resolve, reject) => {
    const tx = db.transaction('documents', 'readonly');
    const request = tx.objectStore('documents').getAll();
    request.onsuccess = () => {
      db.close();
      resolve((request.result as ManagedDocument[]).sort((a, b) => b.createdAt - a.createdAt));
    };
    request.onerror = () => {
      db.close();
      reject(request.error || new Error('Could not load documents.'));
    };
  });
}

async function deleteManagedDocumentById(id: string) {
  const db = await openDocumentDb();
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction('documents', 'readwrite');
    tx.objectStore('documents').delete(id);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error('Could not delete document.'));
    };
  });
}

async function updateManagedDocument(id: string, changes: Partial<Pick<ManagedDocument, 'name' | 'folder'>>) {
  const db = await openDocumentDb();
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction('documents', 'readwrite');
    const store = tx.objectStore('documents');
    const request = store.get(id);
    request.onsuccess = () => {
      const item = request.result as ManagedDocument | undefined;
      if (!item) {
        tx.abort();
        reject(new Error('Document no longer exists on this device.'));
        return;
      }
      store.put({ ...item, ...changes });
    };
    request.onerror = () => reject(request.error || new Error('Could not update document.'));
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error('Could not update document.'));
    };
  });
}

async function updateManagedDocumentFolder(currentFolder: string, nextFolder: string) {
  const db = await openDocumentDb();
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction('documents', 'readwrite');
    const store = tx.objectStore('documents');
    const request = store.getAll();
    request.onsuccess = () => {
      (request.result as ManagedDocument[])
        .filter((item) => item.folder === currentFolder)
        .forEach((item) => store.put({ ...item, folder: nextFolder }));
    };
    request.onerror = () => reject(request.error || new Error('Could not update folder documents.'));
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error('Could not update folder documents.'));
    };
  });
}

function loadRecentOrder() {
  try {
    const raw = window.localStorage.getItem(recentOrderStorageKey);
    if (!raw) return null;
    const order = JSON.parse(raw) as Partial<CloudOrder>;
    return isStoredOrder(order) ? order : null;
  } catch {
    return null;
  }
}

function saveRecentOrder(order: CloudOrder) {
  try {
    window.localStorage.setItem(recentOrderStorageKey, JSON.stringify(order));
  } catch {
    // Tracking is best-effort when browser storage is unavailable.
  }
}

function WordConversionProgress({
  seconds,
  status,
  busy,
  onCancel,
}: {
  seconds: number;
  status: string;
  busy: boolean;
  onCancel: () => void;
}) {
  const converting = status.toUpperCase() === 'CONVERTING';
  const stages = [
    'Document uploaded securely',
    'Contacting shop computer',
    'Preserving Word layout',
    'Preparing PDF preview',
  ];
  const activeIndex = converting ? (seconds < 12 ? 2 : 3) : 1;
  const liveMessage = !converting
    ? seconds < 5
      ? 'Your document is uploaded. Contacting the selected shop computer.'
      : 'Waiting for the shop computer to accept the conversion. No need to tap again.'
    : seconds < 12
      ? 'Microsoft Word is opening the document and preserving its layout.'
      : seconds < 30
        ? 'Creating the PDF and checking the saved page layout.'
        : 'This document is taking longer than usual. Fonts and images can take up to a minute.';
  const progress = converting
    ? Math.min(96, Math.max(50, Math.round(48 + seconds * 1.8)))
    : Math.min(44, Math.max(10, Math.round(10 + seconds * 4)));

  return (
    <div className="payment-preparing-modal word-conversion-progress" role="dialog" aria-modal="true" aria-labelledby="word-conversion-title">
      <div className="payment-preparing-dialog">
        <span className="payment-preparing-spinner" aria-hidden="true"><Icon name="refresh" /></span>
        <strong id="word-conversion-title">Creating your PDF preview</strong>
        <p className="payment-live-message" role="status" aria-live="polite" aria-atomic="true">{liveMessage}</p>
        <div className="payment-progress-track" aria-hidden="true"><span style={{ width: `${progress}%` }} /></div>
        <div className="payment-preparation-steps">
          {stages.map((label, index) => {
            const done = index < activeIndex;
            const active = index === activeIndex;
            return (
              <div className={done ? 'done' : active ? 'active' : 'pending'} key={label}>
                <span aria-hidden="true">{done ? <Icon name="check" /> : active ? <Icon name="clock" /> : index + 1}</span>
                <strong>{label}</strong>
                <small>{done ? 'Done' : active ? 'In progress' : 'Next'}</small>
              </div>
            );
          })}
        </div>
        <small>Usually 10-30 seconds · elapsed {seconds}s. Keep this screen open; it updates automatically.</small>
        <button className="secondary-button danger wide" type="button" disabled={busy} onClick={onCancel}>
          <Icon name="trash" /><span>Cancel conversion</span>
        </button>
      </div>
    </div>
  );
}

function PaymentPreparationProgress({ seconds }: { seconds: number }) {
  const elapsed = Math.max(0, 20 - seconds);
  const stages = [
    { label: 'Order received securely', completeAt: 0 },
    { label: 'Print shop notified', completeAt: 5 },
    { label: 'Payment status checked', completeAt: 11 },
    { label: 'Secure payment link ready', completeAt: 20 },
  ];
  const activeIndex = elapsed < 5 ? 1 : elapsed < 11 ? 2 : 3;
  const liveMessage = elapsed < 5
    ? 'Please wait — contacting your print shop.'
    : elapsed < 11
      ? 'Shop connected. Checking payment status.'
      : elapsed < 17
        ? 'Status checked. Preparing your secure payment link.'
        : 'Almost done. Completing the final secure check.';
  const progress = Math.min(96, Math.max(8, Math.round((elapsed / 20) * 100)));

  return (
    <div className="payment-preparing-modal" role="dialog" aria-modal="true" aria-labelledby="payment-preparing-title">
      <div className="payment-preparing-dialog">
        <span className="payment-preparing-spinner" aria-hidden="true"><Icon name="refresh" /></span>
        <strong id="payment-preparing-title">Setting up your secure payment</strong>
        <p className="payment-live-message" role="status" aria-live="polite" aria-atomic="true">{liveMessage}</p>
        <div className="payment-progress-track" aria-hidden="true"><span style={{ width: `${progress}%` }} /></div>
        <div className="payment-preparation-steps">
          {stages.map((stage, index) => {
            const done = elapsed >= stage.completeAt && index < activeIndex;
            const active = index === activeIndex;
            return (
              <div className={done ? 'done' : active ? 'active' : 'pending'} key={stage.label}>
                <span aria-hidden="true">{done ? <Icon name="check" /> : active ? <Icon name="clock" /> : index + 1}</span>
                <strong>{stage.label}</strong>
                <small>{done ? 'Done' : active ? 'In progress' : 'Next'}</small>
              </div>
            );
          })}
        </div>
        <small>Please stay on this screen. We will continue automatically.</small>
      </div>
    </div>
  );
}

function GatewayHandoffProgress() {
  return (
    <div className="payment-preparing-modal" role="dialog" aria-modal="true" aria-labelledby="gateway-handoff-title">
      <div className="payment-preparing-dialog">
        <span className="payment-preparing-spinner" aria-hidden="true"><Icon name="refresh" /></span>
        <strong id="gateway-handoff-title">Opening secure payment</strong>
        <p className="payment-live-message" role="status" aria-live="polite">Please wait while the payment gateway opens. Tap the payment button only once.</p>
        <small>If you return using Back, the payment button will become available again.</small>
      </div>
    </div>
  );
}

function clearRecentOrder() {
  try {
    window.localStorage.removeItem(recentOrderStorageKey);
  } catch {
    // Tracking is best-effort when browser storage is unavailable.
  }
}

function loadCustomerPhone() {
  try {
    const value = window.localStorage.getItem(customerPhoneStorageKey) || '';
    return /^[6-9]\d{9}$/.test(value) ? value : '';
  } catch {
    return '';
  }
}

function saveCustomerPhone(phone: string) {
  try {
    window.localStorage.setItem(customerPhoneStorageKey, phone);
  } catch {
    // Prefilling the payment number is optional when browser storage is unavailable.
  }
}

function loadOrderHistory() {
  try {
    const raw = window.localStorage.getItem(orderHistoryStorageKey);
    if (!raw) return [];
    const orders = JSON.parse(raw) as Partial<CloudOrder>[];
    if (!Array.isArray(orders)) return [];
    return orders.filter(isStoredOrder).slice(0, maxOrderHistory);
  } catch {
    return [];
  }
}

function saveOrderHistory(orders: CloudOrder[]) {
  try {
    window.localStorage.setItem(orderHistoryStorageKey, JSON.stringify(orders.slice(0, maxOrderHistory)));
  } catch {
    // History is best-effort when browser storage is unavailable.
  }
}

function loadRecentShops(): PublicShop[] {
  try {
    const value = JSON.parse(window.localStorage.getItem(recentShopsStorageKey) || '[]') as PublicShop[];
    return Array.isArray(value) ? value.filter((shop) => shop?.shopId && shop?.shopName).slice(0, maxRecentShops) : [];
  } catch {
    return [];
  }
}

function loadActiveTab(): AppTab {
  try {
    const saved = window.sessionStorage.getItem(activeTabStorageKey);
    return saved === 'docs' || saved === 'orders' || saved === 'profile' ? saved : 'home';
  } catch {
    return 'home';
  }
}

function saveRecentShops(shops: PublicShop[]) {
  try {
    window.localStorage.setItem(recentShopsStorageKey, JSON.stringify(shops.slice(0, maxRecentShops)));
  } catch {
    // Recent shops are optional when local storage is unavailable.
  }
}

function upsertOrderHistory(currentHistory: CloudOrder[], order: CloudOrder) {
  return [
    order,
    ...currentHistory.filter((item) => item.orderId !== order.orderId || item.shopId !== order.shopId),
  ].slice(0, maxOrderHistory);
}

function isStoredOrder(order: Partial<CloudOrder>): order is CloudOrder {
  const conversionOrder = ['CONVERSION_PENDING', 'CONVERTING', 'CONVERSION_READY'].includes(String(order.status || ''));
  return Boolean(
    order.orderId &&
    order.shopId &&
    order.fileName &&
    order.status &&
    (order.paymentReference || (conversionOrder && order.accessToken))
  );
}

createRoot(document.getElementById('root')!).render(<App />);
